import { useState, useEffect, useRef } from 'react'
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { tituloPestana } from '../utils/siglas'
import { useTheme } from '../context/ThemeContext'
import { useQuery } from '@tanstack/react-query'
import BannerHuecosLista from './BannerHuecosLista'
import api, { enVisitaDelSoporte } from '../services/api'
import { useNotifications } from '../hooks/useNotifications'
import { usePermisos } from '../hooks/usePermisos'
import { useIsMobile } from '../hooks/useIsMobile'
import { decideVisibilidad, permisoExplicito } from '../services/visibilidadModulo'
import { ADMIN_SECCIONES, puedeSeccionAdmin } from '../services/permisosAdmin'
import { esAdminDeLaClinica } from '../services/permisosDelServidor'
import { useToast } from '../context/ToastContext'
import { salirConSubidas } from '../utils/nubeSubidasGlobal'
import { alCambiarLaDireccion } from '../utils/apartado'
import AlertasEnvios from './AlertasEnvios'
import ChatWidget from './ChatWidget'
import BuscadorPacienteGlobal from './BuscadorPacienteGlobal'
import MarcaBiango, { MARCA_NOMBRE } from './MarcaBiango'
import { VerComoSelector } from './VerComoRol'
import ChatNotifFlotante from './ChatNotifFlotante'
import { avisar, pedirTexto } from '../context/DialogosContext'
import ErrorBoundary from './ErrorBoundary'
import NotifPersonal from './NotifPersonal'
import AvisoAdminBanner from './AvisoAdminBanner'
// B-360 · el aviso de un pago pendiente con Biango (y la pantalla de cuenta en pausa), en las dos formas del menú.
import AvisoImpago from './AvisoImpago'
import AccesoSoporteBanner from './AccesoSoporteBanner'
import AvisoCitasPorArreglar from './AvisoCitasPorArreglar'
import AvisoCuentaMeta from './AvisoCuentaMeta'   // t360 · los WhatsApp que no salen por la cuenta de la clínica en Meta
// B-672 · las comprobaciones del dia salen EN CUALQUIER PANTALLA, no solo en /recepcion (ver abajo).
import ComprobacionesHoy from './ComprobacionesHoy'
import DentroDeClinica from './DentroDeClinica'
import CampanaAvisos from './CampanaAvisos'
import { ContactoBiango } from '../utils/contactoBiango'
// t271 · el reloj, la cuenta atrás y el cronómetro: el botón de la barra de arriba (un util sin hooks, como el «?»).
import { BotonReloj, FranjaReloj } from '../utils/relojCabecera'
import { abrirReloj } from '../utils/reloj'   // t271b · «Reloj» en el menú del avatar, cuando no cabe en la barra
import { engancharCierre } from '../utils/nubeDesplegables'   // t247 · cerrar el menú de Mi perfil al pulsar fuera
import AvatarPorFirmar from './AvatarPorFirmar'
import RepasoUrgenciasModal from './RepasoUrgenciasModal'
import { useBranding } from '../hooks/useBranding'
import { funcionEncendida } from '../utils/funcionesEstado'   // t279 · una entrada de una función en desarrollo apagada no sale
import { fueraDeLaEdicion } from '../utils/edicionLocalEstado'   // B-176 · en la edición local, lo que depende de internet no sale
import { abrirAsistenteLucia } from '../utils/asistenteLucia'   // t341 · el asistente LucIA del equipo (su panel lo monta App.jsx)
import { veNovedadesDeVersion } from '../utils/novedadesDeVersion'
import { guardarInicioPreferido } from '../services/home'
import { Ico } from './ui'
import { avisoDeSesion } from '../utils/avisoSesion'
import { nombreRol } from '../utils/nombreRol'
import GuiaDePantalla from './GuiaDePantalla'
import ComoSeHace, { ComoSeHaceEnMenu } from './ComoSeHace'   // t578 · «Cómo se hace»: el vídeo de la Formación de cada pantalla, en la cabecera
// t93 · «Cambiar de clínica» en el menú del perfil (un util sin estado: lo cargan de verdad las guardias del Layout).
import { CambioDeClinica, clinicasParaCambiar, pedirCambioDeClinica, errorDelCambio, olvidarLoDeLaAnterior, sidDelToken } from '../utils/cambioDeClinica'
// t95 · «Cambiar de usuario» (la pantalla del PIN): un util sin estado, como el de arriba; sale en las cuentas de la clínica.
import { CambioDeUsuario } from '../utils/pantallaDelPin'
import { MiGabineteEnMenu } from '../utils/MiGabineteEnMenu'   // t530 · B-2268 · «Mi gabinete hoy», en el menú de Mi perfil
// t478 · varias cuentas en la misma app (como Gmail): pasar a otra abierta en el dispositivo y «Añadir otra cuenta» (un util).
import { MenuDeCuentas } from '../utils/MenuDeCuentas'
import { t as tMenu, tx, tDato, marca } from '../utils/i18n'   // t524 · B-2260 · el nombre del laboratorio con el vocabulario del tipo de negocio («Ortopedia» en una podología)
// B-868 · el tamaño del perfil es un zoom en <html> (utils/uiScale): lo medido en la pantalla (ratón, cajas, ventana)
// va en px de pantalla y lo que se escribe en un estilo se pinta multiplicado por el zoom; aquí se pasa a px de estilo.
// Sin import a propósito: las guardias montan este fichero con sus dobles.
const zoomDeLaPagina = () => { try { return parseFloat(document.documentElement.style.zoom) || 1 } catch (_) { return 1 } }

// B-2259 · los nombres y las secciones se quedan aquí en castellano; para otros idiomas, apúntalos también en NOMBRES_DEL_MENU (más abajo).
const navItems = [
  // ORDEN POR MOMENTO DEL DÍA, no por tecnología. Antes había una sección "CHAT" que contenía
  // Pacientes y Presupuestos, y una "DIFUSIÓN" que contenía Agenda, Caja, Cirugías, Laboratorio y
  // Tablón: las etiquetas no describían lo que había dentro y todo acababa cayendo en el cajón.
  //
  // `oculto: true` = la pantalla EXISTE y sigue sujeta a permisos y al guard de módulos, pero no se
  // pinta en el menú porque se llega a ella desde dentro de su módulo (el almacén y sus pestañas).
  { section: 'DÍA A DÍA', items: [
    // INICIO, ARRIBA DEL TODO: es la pantalla común del equipo y la que se mira al llegar. La ven
    // TODOS los roles menos `control_horario` — esa es la cuenta compartida del quiosco de fichar,
    // que vive abierta en el tótem de la entrada y no puede enseñar las ausencias del equipo.
    { to: '/inicio', icon: <Ico n="inicio" s={16} />, label: 'Inicio', roles: ['admin','recepcion','doctor','higienista','colaborador','auxiliar','almacen','limpieza']  },
    // Icono 'grid' y no 'inicio': desde que existe la página de Inicio, dos entradas seguidas con
    // el mismo dibujo se leen como la misma cosa.
    { to: '/recepcion', icon: <Ico n="grid" s={16} />, label: 'Recepción', roles: ['admin','recepcion','auxiliar']  },
    { to: '/agenda', icon: <Ico n="agenda" s={16} />, label: 'Agenda', roles: ['admin','recepcion','doctor','higienista','colaborador','auxiliar']  },
    // t265 · COMUNICACIÓN (antes «WhatsApp»): WhatsApp, el portal del paciente y los laboratorios. El permiso sigue siendo
    // `conversaciones`; /whatsapp y /conversaciones redirigen aquí (App.jsx · RedirComunicacion).
    { to: '/comunicacion', icon: <Ico n="conversaciones" s={16} />, label: 'Comunicación', roles: ['admin','recepcion','auxiliar']  },
    // Chat del EQUIPO. Estaba solo detras del boton de la cabecera: quien no supiera que ese
    // boton existia no encontraba el chat por ningun sitio.
    { to: '/chat', icon: <Ico n="chat" s={16} />, label: 'Chat equipo', roles: ['admin','recepcion','doctor','higienista','colaborador','auxiliar','limpieza']  },
    // B-350 · TAREAS: el tablero del equipo, con las comprobaciones de hoy dentro. El equipo, limpieza incluida (a
    // limpieza también se le ponen comprobaciones); el almacén no (la tablet compartida, B-135) y, desde el 1-oct-2026, el
    // colaborador de serie tampoco: ni esta, ni la Nube, ni Reparaciones le salen de fábrica (backend/services/permisos ·
    // SIN_COLABORADOR_DE_FABRICA). Se las da la clínica, por rol o por persona, en Usuarios, doctores y permisos.
    // B-935 · Y LAS REPARACIONES, EN LA MISMA ENTRADA (29-sep): «Tareas y reparaciones», con una pestaña para cada una y un
    // solo «Nuevo» (components/BarraTareasReparaciones). La ruta no cambia: lo que cada clínica ocultó a un rol va por ruta. t555 (29-oct-2026): se llama «Mantenimiento y controles» (nombre elegido por el dueño).
    { to: '/tareas', icon: <Ico n="clipboard" s={16} />, label: 'Mantenimiento y controles', roles: ['admin','recepcion','doctor','higienista','auxiliar','limpieza']  },
    // LA NUBE (fase 2, docs/NUBE-F2-F4-ESPEC.md): los archivos de la clínica. Los roles, los de su `rolesDefault` efectivo
    // (sin almacén, B-135, ni limpieza, ni el colaborador de serie desde el 1-oct). (P7) `sistema: 'nube'`: el servidor la enciende CLÍNICA A CLÍNICA (NUBE_ACTIVA), así
    // que el menú la pinta solo cuando GET /api/permisos/mias dice que en esta clínica está encendida (`sistemaEncendido`,
    // en moduloVisible). Y en los catálogos que no saben qué clínica miran (MODULOS: Accesos por rol, «Al entrar, llévame
    // a…») cuenta como `oculto`: en una clínica sin la Nube no puede salir en ningún sitio.
    { to: '/nube', icon: <Ico n="nube" s={16} />, label: 'Nube', roles: ['admin','recepcion','doctor','higienista','auxiliar'], sistema: 'nube' },
    // B-135 · SIN 'almacen' (23-sep-2026): de fábrica el almacén ya no trae «Pacientes y citas» (services/permisos,
    // ALMACEN_DE_FABRICA). Con él en la lista, el botón salía mientras cargaban los permisos y se iba al llegar.
    { to: '/pacientes', icon: <Ico n="pacientes" s={16} />, label: 'Pacientes', roles: ['admin','recepcion','doctor','higienista','colaborador','auxiliar']  },
    // t225 (14-oct-2026) · SIN 'auxiliar': de fábrica el auxiliar ya no abre la Caja (services/permisos, clave 'caja'). A la
    // auxiliar a la que la clínica se la dé (a su rol o a ella) le sale igual: el permiso concedido manda.
    { to: '/caja', icon: <Ico n="caja" s={16} />, label: 'Caja', roles: ['admin','recepcion']  },
    // URGENCIAS: quién puede escribir cuando la clínica está cerrada, y el repaso de esa lista. El
    // COLABORADOR entra aquí (ve solo SUS pacientes, acotado en el servidor) porque el repaso es
    // suyo: es a él a quien avisan un domingo.
    //
    // Si a UNA PERSONA se le concede el permiso 'urgencias' (un higienista al que el admin le da
    // acceso sin cambiarle el rol), el botón sale aunque su rol no esté en esta lista. Ya NO hace
    // falta marcarlo entrada por entrada (antes: `porPermiso: true`): lo hace la regla, para los
    // catorce módulos con clave a la vez — ver services/visibilidadModulo.
    { to: '/urgencias', icon: <Ico n="urgencias" s={16} />, label: 'Urgencias', roles: ['admin','recepcion','doctor','colaborador']  },
  ]},
  { section: 'CLÍNICA', items: [
    { to: '/presupuestos', icon: <Ico n="presupuestos" s={16} />, label: 'Presupuestos', roles: ['admin','recepcion','doctor','auxiliar']  },
    { to: '/cirugias', icon: <Ico n="cirugias" s={16} />, label: 'Cirugías', roles: ['admin','recepcion','doctor','colaborador','auxiliar','almacen']  },
    // LABORATORIO es la ÚNICA entrada del menú que no llevaba lista de roles, y por eso acertaba por
    // accidente: sin lista se pinta para todos y el AND del permiso se la quitaba a limpieza… un
    // instante DESPUÉS, cuando llegaba la respuesta. O sea, aparecía y se iba en cada recarga. La
    // lista es la de fábrica de su clave ('laboratorio', R_TODOS_MENOS_LIMP): MEDIDO, con los
    // permisos cargados no cambia ni una entrada para ningún rol — solo deja de parpadear.
    // B-135 · y desde el 23-sep sin 'almacen', que de fábrica ya no trae 'laboratorio'.
    { to: '/laboratorio', icon: <Ico n="laboratorio" s={16} />, label: 'Laboratorio', roles: ['admin','recepcion','doctor','colaborador','auxiliar']  },
    // 1-oct-2026 · AVISOS DE LA AEMPS: lo que publica la Agencia Española de Medicamentos, y a qué pacientes de la clínica
    // les toca (por la medicación que declaran). Los mismos roles que Pacientes, y su misma llave (MODULO_CLAVE): lo que
    // enseña es de sus pacientes.
    { to: '/avisos-aemps', icon: <Ico n="shield" s={16} />, label: 'Avisos de la AEMPS', roles: ['admin','recepcion','doctor','higienista','colaborador','auxiliar']  },
    // B-362 · REPARACIONES: avisar de una avería y seguirla. La ve todo el equipo menos el almacén (la tablet compartida,
    // B-135) y la cuenta del fichaje: quien ve la avería es quien la avisa, también limpieza.
    // B-935 · OCULTA: desde el 29-sep se entra por «Tareas y reparaciones» (su pestaña). Pero `enLugarDe`: a quien la clínica
    // le ha quitado Tareas y no Reparaciones (a limpieza, por ejemplo) le vuelve a salir esta entrada, porque si no se
    // quedaría sin forma de avisar de una avería (seVeEnLugarDe, más abajo).
    { to: '/reparaciones', icon: <Ico n="herramienta" s={16} />, label: 'Reparaciones', roles: ['admin','recepcion','doctor','higienista','auxiliar','limpieza'], oculto: true, enLugarDe: '/tareas' },
    // (El «Tablón» ya no tiene entrada: desde el 11-sep sus documentos y su muro están en Inicio. Su
    // ocultación por rol se sigue gestionando en Ajustes: ver MODULOS, más abajo.)
  ]},
  { section: 'CAPTACIÓN Y SEGUIMIENTO', items: [
    { to: '/seguimiento', icon: <Ico n="notificaciones" s={16} />, label: 'Seguimiento', roles: ['admin','recepcion','auxiliar'] },
    { to: '/recordatorios', icon: <Ico n="notificaciones" s={16} />, label: 'Recordatorios', roles: ['admin','recepcion','auxiliar']  , oculto: true },
    { to: '/recalls', icon: <Ico n="refresh" s={16} />, label: 'Recalls', roles: ['admin','recepcion','auxiliar']  , oculto: true },
    { to: '/lista-espera', icon: <Ico n="clock" s={16} />, label: 'Lista de espera', roles: ['admin','recepcion','auxiliar']  , oculto: true },
    { to: '/citas-anuladas', icon: <Ico n="phoneX" s={16} />, label: 'Anuladas sin reagendar', roles: ['admin','recepcion','auxiliar']  , oculto: true },
    { to: '/encuestas', icon: <Ico n="star" s={16} />, label: 'Encuestas', roles: ['admin']  , oculto: true },
    { to: '/pre-registros', icon: <Ico n="doc" s={16} />, label: 'Pre-registros', roles: ['admin','recepcion','auxiliar']  },
    // REFERIDORES: los pacientes que nos manda otro profesional. Va aquí, en captación, porque eso
    // es lo que son. El COLABORADOR entra (atiende el caso y escribe el informe de vuelta).
    // B-106 · Se llamaba «Derivaciones» hasta el 19-sep-2026. La llave del permiso NO cambia
    // (sigue siendo 'derivaciones', guardada usuario por usuario); cambia el rótulo y la dirección.
    { to: '/referidores', icon: <Ico n="derivaciones" s={16} />, label: 'Referidores', roles: ['admin','recepcion','doctor','colaborador','auxiliar']  },
    { to: '/marketing', icon: <Ico n="marketing" s={16} />, label: 'Marketing', roles: ['admin']  },
  ]},
  // El almacén es UN módulo con sus pantallas dentro (components/StockTabs), no ocho secciones del
  // programa. Las demás siguen aquí para que el permiso y el guard las sigan cubriendo.
  { section: 'ALMACÉN', items: [
    { to: '/stock', icon: <Ico n="almacen" s={16} />, label: 'Almacén', roles: ['admin','almacen']  },
    { to: '/stock/escanear', icon: <Ico n="buscar" s={16} />, label: 'Escanear', roles: ['admin','almacen'] , oculto: true },
    { to: '/stock/alta-rapida', icon: <Ico n="automatismos" s={16} />, label: 'Alta rápida', roles: ['admin','almacen'] , oculto: true },
    { to: '/stock/compra-rapida', icon: <Ico n="cash" s={16} />, label: 'Escanear compra', roles: ['admin','almacen'] , oculto: true },
    { to: '/stock/productos', icon: <Ico n="tag" s={16} />, label: 'Productos', roles: ['admin','almacen'] , oculto: true },
    { to: '/stock/proveedores', icon: <Ico n="users" s={16} />, label: 'Proveedores', roles: ['admin','almacen'] , oculto: true },
    { to: '/stock/ubicaciones', icon: <Ico n="grid" s={16} />, label: 'Ubicaciones', roles: ['admin','almacen'] , oculto: true },
    // ESTA ENTRADA TIENE DOS VIDAS. Para quien tiene el almacén es una pantalla DE DENTRO del módulo
    // (se llega por las pestañas), y por eso está oculta del menú. Para el ENCARGADO de una lista de
    // la compra que NO tiene el almacén es lo ÚNICO que puede abrir (B-102), así que a él —y solo a
    // él— se le pinta en el menú: `abreConEncargoCompras`.
    { to: '/stock/lista-compra', icon: <Ico n="clipboard" s={16} />, label: 'Lista de compra', roles: ['admin','almacen'] , oculto: true, abreConEncargoCompras: true },
  ]},
  { section: 'MI TRABAJO', items: [
    // MI AGENDA: solo se ve si esta clínica tiene la autonomía encendida (el filtro de abajo), o
    // sería una entrada de menú que al pulsarla dice que no está disponible.
    { to: '/mi-agenda', icon: <Ico n="agenda" s={16} />, label: 'Mi agenda', roles: ['admin','doctor','higienista','colaborador','auxiliar'] },
    // MIS CONSENTIMIENTOS (B-436): a donde lleva el WhatsApp de «tienes N consentimientos pendientes».
    // Recepcion esta en la lista por la coordinacion: solo se le pinta si tiene el permiso de ver los de
    // otros doctores (la regla de visibilidad, mas abajo). Sin agenda y sin ese permiso no hay nada que ver.
    { to: '/mis-consentimientos', icon: <Ico n="doc" s={16} />, label: 'Mis consentimientos', roles: ['admin','doctor','higienista','colaborador','auxiliar','recepcion'] },
    { to: '/fichar', icon: <Ico n="fichar" s={16} />, label: 'Fichar', roles: ['admin','recepcion','doctor','higienista','colaborador','auxiliar','almacen','limpieza','control_horario']  },
    { to: '/limpieza', icon: <Ico n="limpieza" s={16} />, label: 'Limpieza', roles: ['admin','limpieza']  },
    // B-1003 · SUGERENCIAS A BIANGO: las ideas de todas las clínicas, que se votan. No es el buzón del equipo (Inicio).
    { to: '/sugerencias', icon: <Ico n="star" s={16} />, label: 'Sugerencias', roles: ['admin','recepcion','doctor','higienista','colaborador','auxiliar','almacen','limpieza'] },
  ]},
  { section: 'ADMINISTRACIÓN', items: [
    { to: '/administracion', icon: <Ico n="ajustes" s={16} />, label: 'Panel de administración', roles: ['admin']  },
    // M9a · EL MIGRADOR (docs/MIGRADOR-ESPEC.md 7.1): traer los datos de otro programa. Solo administración (migrar_datos, DM7).
    // `sistema: 'migracion'`: el servidor lo enciende CLÍNICA A CLÍNICA (MIGRACION_ACTIVA, backend/services/migrador/interruptor),
    // como la Nube: sale solo cuando GET /api/permisos/mias dice que aquí está encendido, y no se ofrece en Accesos por rol.
    { to: '/migracion', icon: <Ico n="upload" s={16} />, label: 'Migrar datos', roles: ['admin'], sistema: 'migracion' },
    { to: '/plantillas', icon: <Ico n="file" s={16} />, label: 'Plantillas', roles: ['admin'] , oculto: true },
    { to: '/plantillas-whatsapp', icon: <Ico n="file" s={16} />, label: 'Plantillas de WhatsApp', roles: ['admin'] , oculto: true },
    { to: '/automatismos', icon: <Ico n="automatismos" s={16} />, label: 'Automatismos', roles: ['admin'] , oculto: true },
  ]},
]

// Catálogo plano de módulos (sin iconos) — lo usa la pantalla de "Accesos por rol" (Ajustes).
// B-935 · todas las entradas del menú, para que seVeEnLugarDe encuentre la que sustituye.
const todasLasEntradas = navItems.flatMap(s => s.items)

// (P7) Una entrada de un SISTEMA que el servidor enciende clínica a clínica (`sistema`, la Nube) cuenta aquí como `oculto`:
// este catálogo es estático y no sabe qué clínica mira, así que no puede ofrecerla en las que no la tienen.
export const MODULOS = navItems.flatMap(s => s.items.map(i => ({
  ruta: i.to, label: i.label, section: s.section, oculto: !!i.oculto || !!i.sistema,
  roles: i.roles || null, soloSuperadmin: !!i.soloSuperadmin, soloEmail: i.soloEmail || null,
  // (1-oct-2026) El sistema del que es la entrada: «Accesos por rol» la ofrece en las clínicas que lo tienen (Ajustes).
  sistema: i.sistema || null,
})))

// Y UNA ENTRADA QUE NO ES DEL MENÚ: «Documentos del equipo (en Inicio)». La pantalla que había se fundió con
// Inicio (entrega 4 de Mis documentos y Tablón, 11-sep-2026), pero una clínica que se los ocultó a un
// rol lo decidió por algo, y esa decisión está guardada POR RUTA ('/tablon'). Sin esta entrada, Ajustes
// dejaría de ofrecerla, useModuloVisible no sabría resolverla y los documentos le saldrían en Inicio a
// quien la clínica se los quitó. Los roles son los que tenía aquella entrada. `soloAjustes`: se
// gestiona en «Accesos por rol», pero no es una pantalla a la que entrar (no sale en «Al entrar, llévame a…»).
//
// LA RUTA NO SE RENOMBRA NUNCA, SOLO EL RÓTULO (B-695, 19-sep-2026). Lo que cada clínica ocultó o
// mostró a cada rol está guardado en `rol_modulo_oculto.ruta = '/tablon'`: cambiar la llave le
// devolvería el acceso a quien se lo quitaron, sin avisar a nadie. Es el mismo patrón que B-106
// («Derivaciones» pasó a llamarse «Referidores» y la llave del permiso se quedó en `derivaciones`).
const ROLES_DOCUMENTOS_TABLON = ['admin','recepcion','doctor','higienista','colaborador','auxiliar','almacen','limpieza']
MODULOS.push({ ruta: '/tablon', label: 'Documentos del equipo (en Inicio)', section: 'DÍA A DÍA', oculto: false,
  roles: ROLES_DOCUMENTOS_TABLON, soloSuperadmin: false, soloEmail: null, soloAjustes: true })

// Ruta de menú → clave de permiso funcional. UN PERMISO EXPLÍCITO SOBRE ESTA CLAVE MANDA: concedido
// a esa persona o a su rol, ABRE el módulo aunque su rol no esté en la lista `roles` de arriba;
// denegado, lo CIERRA (services/visibilidadModulo).
//
// LA LISTA `roles` DE CADA ENTRADA SIGUE SIENDO QUIÉN VE EL MÓDULO DE FÁBRICA, y NO tiene que
// coincidir con el `rolesDefault` de la clave en backend/services/permisos.js. Donde difieren hoy
// —el catálogo trae 'almacen' marcado en cinco claves que el menú no le pinta— es un default que
// revisará el dueño («referente al rol ya te diré cómo poner por defecto cada rol»), no algo que
// arregle un despliegue: alinearlas aquí le estrenaría cinco módulos, el WhatsApp de los pacientes
// entre ellos. Lo único que sí se exige es que la lista NO SEA MÁS ANCHA que el `rolesDefault`, o el
// botón saldría mientras cargan los permisos y se iría al llegar; lo mide la guardia
// backend/routes/permisos-el-permiso-manda.test.js.
export const MODULO_CLAVE = {
  '/chat': 'chat',
  '/comunicacion': 'conversaciones', '/whatsapp': 'conversaciones', '/pacientes': 'clientes', '/agenda': 'clientes', '/avisos-aemps': 'clientes', '/presupuestos': 'presupuestos',
  '/marketing': 'marketing', '/plantillas': 'plantillas', '/plantillas-whatsapp': 'plantillas', '/automatismos': 'automatismos',
  '/informes': 'informes', '/catalogo-tratamientos': 'catalogo', '/mapear-motivos': 'catalogo', '/asistencias': 'asistencias',
  '/pre-registros': 'pre_registros', '/limpieza': 'limpieza', '/laboratorio': 'laboratorio', '/reparaciones': 'reparaciones', '/tareas': 'tareas', '/nube': 'nube',
  '/stock': 'stock', '/stock/escanear': 'stock', '/stock/alta-rapida': 'stock', '/stock/compra-rapida': 'stock',
  '/stock/productos': 'stock', '/stock/proveedores': 'stock', '/stock/ubicaciones': 'stock', '/stock/lista-compra': 'stock',
  '/cirugias': 'cirugias',
  // Caja: su ruta se gobierna por el permiso 'caja'. Sin esta línea, denegar "Caja diaria" a un rol
  // dejaba el botón puesto en el menú y al pulsarlo se devolvía al usuario a su pantalla de inicio.
  // Desde el 16-sep-2026 (B-529) la pantalla vive en la aplicación, no en el panel de administración:
  // el botón abre la Caja aquí mismo. El default de menú (admin/recepción/auxiliar) es el mismo que
  // el del permiso, así que por defecto no cambia nada.
  '/caja': 'caja',
  // Seguimiento NO lleva permiso propio a propósito: cada PESTAÑA comprueba el de su módulo
  // original (así un "oculta Recalls a recepción" se sigue respetando dentro), y si no le queda
  // ninguna, la pantalla se lo dice. Ponerle uno aquí podría dejar fuera a quien tiene una de las
  // cuatro pero no el permiso general.
  '/lista-espera': 'lista_espera', '/encuestas': 'encuestas',
  // Referidores: quién entra lo decide el permiso (por rol y por usuario), igual que Urgencias.
  // B-106 · LAS DOS DIRECCIONES APUNTAN A LA MISMA LLAVE. La pantalla se llama ahora «Referidores»
  // y vive en `/referidores`, pero `/derivaciones` sigue existiendo (redirige) porque los avisos ya
  // enviados la llevan dentro. Si la vieja no estuviera aquí, quien abriera uno de esos avisos
  // pasaría por una ruta sin clave: ni se le abriría por permiso ni se le cerraría por permiso.
  '/referidores': 'derivaciones',
  '/derivaciones': 'derivaciones',
  // Urgencias: quién entra lo decide el permiso (por rol y por usuario), no el rol a secas. Sin esta
  // línea, denegar Urgencias a un rol dejaba el botón puesto y la página devolvía 403 al pulsarlo.
  '/urgencias': 'urgencias',
  // (M9a · /migracion NO va aquí: es un `sistema` que el servidor enciende clínica a clínica, MIGRACION_ACTIVA, y su clave
  // migrar_datos no se delega (DM7). Su ruta la cierra PermRoute y su entrada, `sistema: 'migracion'` en navItems.)
}

// B-935 · VA AQUÍ, DETRÁS DE MODULO_CLAVE, A PROPÓSITO: varias guardias recortan este fichero desde `navItems` hasta
// `MODULO_CLAVE` y lo ejecutan (test-helpers/inicioEnPantalla · catalogoDeVerdad); una función exportada en medio las rompe.
// B-935 · ¿SE PINTA ESTA ENTRADA OCULTA EN LUGAR DE OTRA? Solo si la otra (`enLugarDe`) NO se ve: entonces esta vuelve a
// salir con su nombre de siempre. Se pregunta con la misma regla que el resto del menú (`visible`, la de moduloVisible).
// Una entrada sin `enLugarDe` no se pinta por aquí nunca. Lo ejecuta routes/tareas-y-reparaciones-juntas.test.js.
export function seVeEnLugarDe(entrada, entradas, visible) {
  if (!entrada || !entrada.enLugarDe) return false
  const otra = (entradas || []).find((m) => m.to === entrada.enLugarDe)
  return !otra || !visible(otra)
}
// Y las rutas que tienen entrada propia aunque vayan ocultas (hoy, /reparaciones): Ajustes → Accesos por rol las sigue
// ofreciendo, porque ocultar ahí también CIERRA el permiso (B-135) y sin su fila no habría dónde quitárselas a un rol.
export const EN_LUGAR_DE = todasLasEntradas.filter((i) => i.enLugarDe).map((i) => i.to)

// t85 (4-oct-2026) · LA CABECERA SE APRIETA CUANDO NO CABE, MIDIÉNDOLA. El corte fijo de 1000 px (`compacto`) se midió
// con los botones que había entonces; después llegaron el «?», el megáfono del Busca y el «Ver como…» del equipo de Biango,
// y en el navegador de verdad la fila se salía por la derecha: a 1366 y 1280 px (superadmin dentro de una clínica) y a
// 1024 px (administración, el iPad en horizontal) desaparecían la campana y el avatar, sin barra que lo dijera. Ahora se
// mide lo pintado: si la fila no cabe o al buscador le quedan menos de BUSCADOR_MIN px, se aprieta un nivel (1: sin los
// rótulos ni el nombre de la página; 2: además sin el «Ver como…»). Se recuerda con qué ancho no cupo, y al ensanchar
// la ventana (o achicar el tamaño de letra) por encima de él se vuelve a probar entera. Una medida solo aprieta desde el
// nivel con el que se tomó (`m.nivel`): varias a la vez (el efecto y el ResizeObserver) no saltan dos niveles. Función
// pura: la mide una guardia.
//
// t271b (16-oct-2026) · Y CUANDO NI ASÍ CABE, LO QUE SOBRA AL MENÚ DEL AVATAR, POR NIVELES: 3, el «?» y el megáfono del
// Busca; 4, el tema; 5, Modo doctor y Modo auxiliar; 6, el reloj (lo que está en marcha sale entonces en una franja debajo
// de la barra, utils/relojCabecera · FranjaReloj); y t578, 7, el «Cómo se hace» de la pantalla (components/ComoSeHace: su entrada en
// el menú abre lo mismo). Es lo que ya pasaba por debajo de 560 px (B-050), ahora solo donde de
// verdad no cabe: en una tablet en vertical con el menú lateral abierto y un administrador con todos los botones, el
// buscador de pacientes se quedaba en la lupa. Y «no cabe» incluye que el rótulo del propio campo («Buscar paciente…», o
// «Buscar» en el móvil) se corte (`rotuloCortado`, medido en el campo de verdad). Por debajo de 769 px el buscador es el
// del móvil (se abre a todo el ancho al tocarlo) y le basta un hueco menor (BUSCADOR_MIN_MOVIL).
// Lo ejecuta, nivel a nivel, routes/cabecera-cabe-en-tablet-y-movil.test.js.
const BUSCADOR_MIN = 200
const BUSCADOR_MIN_MOVIL = 120
const CORTE_BUSCADOR_MOVIL = 769
export function siguienteNivelCabecera(prev, m) {
  const p = prev && typeof prev.nivel === 'number' ? prev : { nivel: 0, ancho: 0 }
  if (!m || !(m.ancho > 0)) return p
  if (p.nivel > 0 && m.ancho > p.ancho) return { nivel: 0, ancho: 0 }
  if (m.noCabe && p.nivel < 7 && (m.nivel == null || m.nivel === p.nivel)) return { nivel: p.nivel + 1, ancho: Math.max(p.ancho, m.ancho) }
  return p
}
// ¿El rótulo del campo del buscador no cabe en él? Se mide el <input> pintado (su ancho sin rellenos) contra lo que ocupa
// su texto con su letra. Sin campo, sin rótulo o sin navegador, no.
let _lienzoRotulo = null
function rotuloCortado(hueco) {
  try {
    const campo = hueco && typeof hueco.querySelector === 'function' ? hueco.querySelector('input') : null
    if (!campo || !campo.placeholder || typeof window.getComputedStyle !== 'function' || typeof document === 'undefined') return false
    const cs = window.getComputedStyle(campo)
    const sitio = campo.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0)
    if (!_lienzoRotulo) _lienzoRotulo = document.createElement('canvas')
    const ctx = _lienzoRotulo.getContext('2d')
    if (!ctx) return false
    ctx.font = cs.font || [cs.fontStyle, cs.fontWeight, cs.fontSize, cs.fontFamily].filter(Boolean).join(' ')
    return sitio + 1 < ctx.measureText(campo.placeholder).width
  } catch (_) { return false }
}
// Para las guardias: montar la cabecera ya en un nivel (el navegador siempre empieza en el 0 y aprieta midiendo).
export const AJUSTE_CABECERA = { nivelInicial: 0 }
function useCabeceraApretada(filaRef, huecoRef, accionesRef) {
  const [estado, setEstado] = useState(() => ({ nivel: Number(AJUSTE_CABECERA.nivelInicial) || 0, ancho: 0 }))
  useEffect(() => {
    const medir = () => {
      const fila = filaRef.current
      const hueco = huecoRef.current
      if (!fila || typeof fila.scrollWidth !== 'number' || typeof window === 'undefined') return
      let z = 1
      try { z = parseFloat(document.documentElement.style.zoom) || 1 } catch (_) { z = 1 }
      const movil = window.innerWidth / z < CORTE_BUSCADOR_MOVIL
      const noCabe = fila.scrollWidth > fila.clientWidth + 1
        || !!(hueco && typeof hueco.clientWidth === 'number' && (movil ? hueco.clientWidth < BUSCADOR_MIN_MOVIL : hueco.clientWidth < BUSCADOR_MIN))
        || rotuloCortado(hueco)
      const nivel = estado.nivel
      setEstado((prev) => { const n = siguienteNivelCabecera(prev, { ancho: window.innerWidth / z, noCabe, nivel }); return n.nivel === prev.nivel && n.ancho === prev.ancho ? prev : n })
    }
    medir()
    let ro = null
    try {
      if (typeof ResizeObserver === 'function') {
        ro = new ResizeObserver(() => medir())
        for (const r of [filaRef, accionesRef]) if (r.current) ro.observe(r.current)
      }
    } catch (_) { ro = null }
    window.addEventListener('resize', medir)
    window.addEventListener('biango:zoom', medir)
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', medir); window.removeEventListener('biango:zoom', medir) }
  }, [estado.nivel, filaRef, huecoRef, accionesRef])
  return estado.nivel
}

// t251 (15-oct-2026) · EL AVISO LLAMATIVO DEL SOPORTE, también trabajando en la clínica (el equipo de Biango pasa aquí el
// día): components/AvisoSoporteNuevo, cargado aparte. Solo se monta para `es_superadmin`; la sección de soporte la mira el
// servidor (sin ella, 403 y no se pinta nada).
const cargarAvisoSoporte = () => import('./AvisoSoporteNuevo')
function AvisoSoporteDelEquipo() {
  const [Aviso, setAviso] = useState(null)
  useEffect(() => {
    let vivo = true
    cargarAvisoSoporte().then((m) => { if (vivo) setAviso(() => m.default) }).catch(() => {})
    return () => { vivo = false }
  }, [])
  return Aviso ? <Aviso /> : null
}

// t246f (16-oct-2026) · «GUARDA TU FIRMA PARA LOS CONSENTIMIENTOS»: al doctor que aún no tiene su firma guardada (ni un
// certificado vigente) se la pide una ventana que no se salta, al entrar y al abrir un consentimiento (nunca en medio de otra
// tarea). components/FirmaObligatoria, cargado aparte desde Layout (su estado, `VentanaFirma`); quién la tiene pendiente lo
// dice el servidor. En una visita del soporte no se carga: no es su firma.
const cargarFirmaObligatoria = () => import('./FirmaObligatoria')

// t93 · EL CÓDIGO DE LA APP AL CAMBIAR A UNA CLÍNICA CUYA CUENTA LO PIDE (las del equipo de Biango): el MISMO paso que en
// la pantalla de entrar (components/SegundoFactorEntrada), cargado aparte como allí. Solo se monta cuando hace falta.
const cargarSegundoFactorDelCambio = () => import('./SegundoFactorEntrada')
function SegundoFactorDelCambio({ datos, clinica, onEntrar, onVolver }) {
  const [Paso, setPaso] = useState(null)
  const [fallo, setFallo] = useState(false)
  useEffect(() => {
    let vivo = true
    cargarSegundoFactorDelCambio().then((m) => { if (vivo) setPaso(() => m.default) }).catch(() => { if (vivo) setFallo(true) })
    return () => { vivo = false }
  }, [])
  return (
    <div className="modal-safe" style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div role="dialog" aria-modal="true" aria-label={tMenu('components.Layout.entrar_en', 'Entrar en {{x}}', { x: clinica?.nombre || tMenu('components.Layout.la_otra_clinica', 'la otra clínica') })}
        style={{ background: 'var(--surface)', color: 'var(--text-primary)', borderRadius: '16px', padding: '20px 20px 16px', width: '100%', maxWidth: '420px', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '12px' }}>
          {tMenu('components.Layout.para_entrar_en', 'Para entrar en ')}<b>{clinica?.nombre}</b>{tMenu('components.Layout.hace_falta_el_codigo_de', ' hace falta el código de tu app de autenticación.')}
        </div>
        {fallo
          ? <div role="alert" style={{ color: 'var(--danger)', fontSize: '13px' }}>{tMenu('components.Layout.no_se_ha_podido_cargar', 'No se ha podido cargar el paso del código. Recarga la página.')}</div>
          : Paso ? <Paso datos={datos} onEntrar={onEntrar} onVolver={onVolver} />
            : <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{tMenu('components.Layout.un_momento', 'Un momento…')}</div>}
      </div>
    </div>
  )
}

// B-2259 (t590) · LOS NOMBRES DEL MENÚ, EN EL DICCIONARIO. navItems y MODULOS siguen en castellano: los leen el servidor, los
// permisos, Ajustes y muchas guardias, y una entrada se reconoce por su ruta, no por cómo se llama. Aquí solo se apuntan sus
// nombres con marca() (devuelve el mismo castellano) para que el diccionario los traiga, y el menú los pinta con tDato(): en
// castellano, lo mismo de siempre; en otro idioma, su traducción. Si añades o cambias una entrada del menú, pon aquí su nombre:
// lo mide backend/routes/i18n-menu-con-sus-nombres.test.js.
export const NOMBRES_DEL_MENU = [
  marca('components.Layout.seccion_dia_a_dia', 'DÍA A DÍA'),
  marca('components.Layout.seccion_clinica', 'CLÍNICA'),
  marca('components.Layout.seccion_captacion_y_seguimiento', 'CAPTACIÓN Y SEGUIMIENTO'),
  marca('components.Layout.seccion_almacen', 'ALMACÉN'),
  marca('components.Layout.seccion_mi_trabajo', 'MI TRABAJO'),
  marca('components.Layout.seccion_administracion', 'ADMINISTRACIÓN'),
  marca('components.Layout.menu_inicio', 'Inicio'),
  marca('components.Layout.menu_recepcion', 'Recepción'),
  marca('components.Layout.menu_agenda', 'Agenda'),
  marca('components.Layout.menu_comunicacion', 'Comunicación'),
  marca('components.Layout.menu_chat_equipo', 'Chat equipo'),
  marca('components.Layout.menu_mantenimiento_y_controles', 'Mantenimiento y controles'),
  marca('components.Layout.menu_nube', 'Nube'),
  marca('components.Layout.menu_pacientes', 'Pacientes'),
  marca('components.Layout.menu_caja', 'Caja'),
  marca('components.Layout.menu_urgencias', 'Urgencias'),
  marca('components.Layout.menu_presupuestos', 'Presupuestos'),
  marca('components.Layout.menu_cirugias', 'Cirugías'),
  marca('components.Layout.menu_laboratorio', 'Laboratorio'),
  marca('components.Layout.menu_avisos_de_la_aemps', 'Avisos de la AEMPS'),
  marca('components.Layout.menu_reparaciones', 'Reparaciones'),
  marca('components.Layout.menu_seguimiento', 'Seguimiento'),
  marca('components.Layout.menu_recordatorios', 'Recordatorios'),
  marca('components.Layout.menu_recalls', 'Recalls'),
  marca('components.Layout.menu_lista_de_espera', 'Lista de espera'),
  marca('components.Layout.menu_anuladas_sin_reagendar', 'Anuladas sin reagendar'),
  marca('components.Layout.menu_encuestas', 'Encuestas'),
  marca('components.Layout.menu_pre_registros', 'Pre-registros'),
  marca('components.Layout.menu_referidores', 'Referidores'),
  marca('components.Layout.menu_marketing', 'Marketing'),
  marca('components.Layout.menu_almacen', 'Almacén'),
  marca('components.Layout.menu_escanear', 'Escanear'),
  marca('components.Layout.menu_alta_rapida', 'Alta rápida'),
  marca('components.Layout.menu_escanear_compra', 'Escanear compra'),
  marca('components.Layout.menu_productos', 'Productos'),
  marca('components.Layout.menu_proveedores', 'Proveedores'),
  marca('components.Layout.menu_ubicaciones', 'Ubicaciones'),
  marca('components.Layout.menu_lista_de_compra', 'Lista de compra'),
  marca('components.Layout.menu_mi_agenda', 'Mi agenda'),
  marca('components.Layout.menu_mis_consentimientos', 'Mis consentimientos'),
  marca('components.Layout.menu_fichar', 'Fichar'),
  marca('components.Layout.menu_limpieza', 'Limpieza'),
  marca('components.Layout.menu_sugerencias', 'Sugerencias'),
  marca('components.Layout.menu_panel_de_administracion', 'Panel de administración'),
  marca('components.Layout.menu_migrar_datos', 'Migrar datos'),
  marca('components.Layout.menu_plantillas', 'Plantillas'),
  marca('components.Layout.menu_plantillas_de_whatsapp', 'Plantillas de WhatsApp'),
  marca('components.Layout.menu_automatismos', 'Automatismos'),
  marca('components.Layout.menu_documentos_del_equipo_en_inicio', 'Documentos del equipo (en Inicio)'),
]
export default function Layout() {
  const { user, logout, login, realUser } = useAuth()
  const branding = useBranding()
  // 7-oct-2026 · la versión del menú lleva a «Novedades por versión» solo a quien puede verlas: por ahora, el equipo de
  // Biango (utils/novedadesDeVersion). A una clínica se le enseña el número, que es lo que pregunta soporte, sin enlace.
  const veNovedades = veNovedadesDeVersion(user)
  // ¿Es esto una visita del soporte a otra clinica? (services/api, B-504). Se lee en cada pintado: al
  // entrar o salir de una clinica, useBranding repinta esta pantalla.
  const soporteDeVisita = typeof enVisitaDelSoporte === 'function' && enVisitaDelSoporte()
  // t246f · la ventana «Guarda tu firma para los consentimientos» (ver `cargarFirmaObligatoria`, arriba).
  const [VentanaFirma, setVentanaFirma] = useState(null)
  useEffect(() => {
    if (soporteDeVisita) return undefined
    let vivo = true
    cargarFirmaObligatoria().then((m) => { if (vivo) setVentanaFirma(() => m.default) }).catch(() => {})
    return () => { vivo = false }
  }, [soporteDeVisita])
  const { dark, toggle: toggleTema } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  // Heredar el esquema de marcación (softphone) que fijó el admin a nivel clínica,
  // para que el botón Llamar funcione en TODOS los equipos (recepción incluida) sin
  // tener que entrar a Ajustes.
  useEffect(() => {
    api.get('/citas/config-llamadas').then(r => {
      if (r.data?.dial_protocol) localStorage.setItem('dial_protocol', r.data.dial_protocol)
      localStorage.setItem('dial_prefijo_mas', r.data?.dial_prefijo_mas ? '1' : '0')
    }).catch(() => {})
  }, [])
  // ── LA CABECERA EN COMPACTO: SOLO ICONOS MIENTRAS NO QUEPAN LOS ROTULOS ───────────────────
  // Los rotulos de la cabecera («Chat equipo», «Modo doctor», «Modo auxiliar», el selector de rol
  // y el nombre de la pagina) iban con la clase `hide-mobile`, que los esconde por debajo de
  // 768 px. Entre 769 y ~1000 px seguian pintados Y NO CABIAN: midiendo la cabecera montada, en un
  // iPad vertical (834 px) al buscador de pacientes le quedaban 19 px de caja —o sea que al texto le
  // quedaban -45: no cabia ni una letra—, y a 769 px se quedaba en CERO y la fila se salia 22 px por
  // la derecha del contenedor, que lleva `overflow: hidden`: el avatar desaparecia sin una barra de
  // desplazamiento que lo dijera.
  // El corte NO es de gusto, esta MEDIDO (`routes/buscador-app-ancho.test.js`): con los rotulos
  // puestos, el rotulo del propio campo («Buscar paciente…», 103 px) no cabe hasta los 999 px de
  // pantalla; escondiendolos, cabe desde 769. De ahi el 1000.
  const compacto = useIsMobile(1000)
  // B-050 · EN UN MÓVIL NO CABE TODO. El dueño: «incluso yo como admin no veo todos los iconos superiores de la
  // cabecera». A un admin le tocan a la derecha el tema, el chat, Modo doctor, Modo auxiliar, la campana y su avatar,
  // todos sin encogerse: en un teléfono de 360 px —y menos con la pantalla ampliada— lo último se salía y no se veía.
  // Por debajo de 560 px, el tema y los dos modos pasan al menú del avatar (routes/buscador-app-ancho.test.js §7).
  const estrecho = useIsMobile(560)
  const cabeceraRef = useRef(null)
  const huecoBuscadorRef = useRef(null)
  const accionesCabeceraRef = useRef(null)
  const apretado = Number(useCabeceraApretada(cabeceraRef, huecoBuscadorRef, accionesCabeceraRef)) || 0
  const sinRotulos = compacto || apretado >= 1
  const sinVerComo = compacto || apretado >= 2
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // Colapsar el sidebar en ESCRITORIO (persistente). En móvil se usa sidebarOpen (overlay).
  const [sidebarColapsado, setSidebarColapsado] = useState(() => {
    try { return localStorage.getItem('sidebarColapsado') === '1' } catch { return false }
  })
  // El botón hamburguesa: en escritorio colapsa/expande el sidebar; en móvil abre/cierra el overlay.
  const toggleSidebar = () => {
    if (typeof window !== 'undefined' && window.innerWidth / zoomDeLaPagina() > 768) {   // B-868 · el mismo corte que a-min-769
      setSidebarColapsado(c => { const n = !c; try { localStorage.setItem('sidebarColapsado', n ? '1' : '0') } catch (_) {} ; return n })
    } else {
      setSidebarOpen(o => !o)
    }
  }
  const [perfilOpen, setPerfilOpen] = useState(false)
  // N15: badge de mensajes sin leer para el botón de "Chat equipo" de la cabecera (misma key que el widget).
  // B-160 · TAMBIÉN CON LA PESTAÑA EN SEGUNDO PLANO (`refetchIntervalInBackground`). El número va al
  // título de la pestaña (ver el efecto del título), que es lo único que se ve de la aplicación
  // cuando se está en otra; y React Query, por defecto, deja de preguntar justo entonces. Es una
  // cuenta ligera y el navegador ya espacia los temporizadores de una pestaña escondida.
  const { data: chatBadge } = useQuery({ queryKey: ['chat-badge'], queryFn: () => api.get('/chat/no-leidos').then(r => r.data).catch(() => ({ total: 0 })), refetchInterval: 20000, refetchIntervalInBackground: true })
  // SOLO lo del EQUIPO. Antes pintaba el total (equipo + soporte): si lo no leido era una consulta
  // de soporte, este boton mostraba un numero rojo, lo abrias, el chat de equipo no ensena el
  // soporte, no habia nada que leer y el numero NO BAJABA NUNCA. Era el "me sigue fallando".
  const chatNoLeidos = chatBadge?.equipo ?? chatBadge?.total ?? 0
  const { perms, permiso, baseRol, permisosListos, idsUsu: idsUsuFresh } = usePermisos()
  // ¿ES ADMINISTRADORA DE ESTA CLINICA? SE PREGUNTA, NO SE COPIA — y se pregunta UNA VEZ, aqui.
  // Vivia escrita a mano tres veces en este mismo fichero (el badge de asistencias, la entrada al
  // panel y el guard suave del menu) con la frase que el servidor acaba de quitar de 32 routers:
  // `user?.rol === 'admin' || user?.es_superadmin === true`. Medido por HTTP, a una cuenta de
  // plataforma con CERO secciones `/api/administracion/resumen` y `/api/admin/dashboard` le
  // contestan el MISMO 403 que a su companera de rol, y aquellas lineas le pintaban igualmente
  // «Panel de administracion»: una puerta con nada detras. La decision la manda el servidor ya
  // resuelta (`_es_admin`, la misma con la que las rutas dan ese 403) y aqui solo se lee.
  const esAdminPanel = esAdminDeLaClinica(perms)
  // Rol EFECTIVO para el MENÚ: un rol personalizado hereda la visibilidad de su rol de serie (base_rol).
  const rolMenu = user?.base_rol || baseRol || user?.rol
  // M1: "Modo doctor" a pantalla completa (kiosko): sin sidebar ni cabecera normal, solo la agenda del
  // doctor + una barra superior mínima con perfil y salir. Visible para admin y doctores con agenda.
  const kiosko = location.pathname === '/doctor'
  // t271b · LO QUE PASA AL MENÚ DEL AVATAR (ver siguienteNivelCabecera): por debajo de 560 px, siempre (B-050); en la
  // cabecera de la app, además, cuando la medida dice que no cabe. La barra del Modo doctor no se mide: ahí, el corte fijo.
  const nivelMenu = kiosko ? 0 : apretado
  const ayudaAlMenu = estrecho || nivelMenu >= 3
  const temaAlMenu = estrecho || nivelMenu >= 4
  const modosAlMenu = estrecho || nivelMenu >= 5
  const relojAlMenu = nivelMenu >= 6
  const comoAlMenu = nivelMenu >= 7   // t578 · el «Cómo se hace» de la pantalla, lo último que sale de la barra
  // Los "modos" se gobiernan por PERMISO (configurable por usuario en Administración), no por rol:
  // así el admin puede dar "Modo auxiliar" a un higienista colaborador sin cambiarle el rol.
  // Modo doctor además necesita una agenda vinculada (idsUsu) porque muestra la agenda propia.
  // Agendas: las del servidor mandan sobre las del login guardado (ver usePermisos). Mientras
  // cargan (null) se usan las del navegador, para que el botón no parpadee.
  const _agendas = idsUsuFresh !== null ? idsUsuFresh : (Array.isArray(user?.idsUsu) ? user.idsUsu : [])
  const _tieneAgenda = user?.rol === 'admin' || _agendas.length > 0
  // El permiso y la agenda son cosas distintas: con permiso pero sin agenda el botón se veía y
  // desaparecía sin explicar nada, y quien lo estaba configurando no tenía forma de saber que le
  // faltaba asignarle la agenda. Ahora sale desactivado y lo dice.
  const puedeModoDoctor = permiso('modo_doctor')
  const modoDoctorSinAgenda = puedeModoDoctor && !_tieneAgenda
  const puedeModoAuxiliar = permiso('modo_auxiliar')   // M2
  // Secciones del sidebar colapsables — todas cerradas por defecto, persiste lo que abre el usuario.
  const [secsAbiertas, setSecsAbiertas] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('sidebarSecsAbiertas') || '[]')) }
    catch { return new Set() }
  })
  const toggleSec = (s) => {
    setSecsAbiertas(prev => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s); else next.add(s)
      try { localStorage.setItem('sidebarSecsAbiertas', JSON.stringify([...next])) } catch (_) {}
      return next
    })
  }
  const perfilRef = useRef()
  // t247 (15-oct-2026) · EL MENÚ DE MI PERFIL SE CIERRA AL PULSAR O TOCAR FUERA Y CON ESCAPE (el dueño: «que al pulsar fuera se
  // cierre [...] lo mismo con el modal de Mi Perfil»). Solo se cerraba volviendo a pulsar el avatar. La regla de la casa
  // (utils/nubeDesplegables · engancharCierre), la misma que la campana y «Opciones» de la agenda: «dentro» es todo lo del
  // avatar y su menú (también «Contactar con Biango», que en el móvil va dentro y se despliega aquí).
  useEffect(() => {
    if (!perfilOpen) return undefined
    return engancharCierre({ dentro: (t) => !!(perfilRef.current && perfilRef.current.contains && perfilRef.current.contains(t)), cerrar: () => setPerfilOpen(false) })
  }, [perfilOpen])
  useNotifications()
  const { data: contadores = {} } = useQuery({
    queryKey: ['layout-contadores'],
    queryFn: () => api.get('/conversaciones/contadores').then(r => r.data).catch(() => ({})),
    refetchInterval: 15000,
    // t85 (4-oct-2026) · solo a quien tiene las conversaciones: a un doctor le daba 403 cada 15 s toda la sesión.
    enabled: permisosListos && permiso('conversaciones'),
  })
  // t265 · los mensajes de los laboratorios sin leer suman en el número de Comunicación (la misma consulta que la pastilla
  // «Laboratorios» de la bandeja: una petición para los dos). Solo a quien ve Comunicación.
  const { data: labSinLeer = {} } = useQuery({
    queryKey: ['lab-sin-leer'],
    queryFn: () => api.get('/comunicacion-laboratorio/sin-leer').then(r => r.data).catch(() => ({ total: 0 })),
    refetchInterval: 30000,
    enabled: permisosListos && permiso('conversaciones'),
  })
  // Nº de productos a comprar (manual + bajo mínimo) → badge en "Lista de compra". Solo admin.
  // Por PERMISO, no por lista de roles: el responsable de compras puede ser una auxiliar o una
  // higienista: con la lista de roles nunca se le disparaba la consulta y no veía su badge.
  const { data: stockContador = {} } = useQuery({
    queryKey: ['stock-lista-compra-contador'],
    queryFn: () => api.get('/stock/lista-compra/contador').then(r => r.data).catch(() => ({})),
    refetchInterval: 60000,
    // También al ENCARGADO de una lista que no tiene el almacén: es su badge, y sin esto la única
    // pantalla que puede abrir no le avisa de nada (B-102).
    enabled: perms?.stock === true || perms?._compras_encargado === true,
  })
  // MATERIAL DE CIRUGÍA QUE ME TOCA COMPRAR A MÍ. Solo lo suyo: si nadie le ha nombrado responsable
  // de ninguna marca, sale 0 y no hay badge. Es el aviso de "hay que pedir esto" que se pidió.
  const { data: cirugiasCompras } = useQuery({
    queryKey: ['cirugias-compras-mias'],
    queryFn: () => api.get('/cirugias/compras/mias').then(r => r.data).catch(() => ({ pendientes: 0 })),
    refetchInterval: 60000,
    enabled: perms?.cirugias === true,
  })
  // B-839 · LAS GUÍAS QUIRÚRGICAS QUE ME TOCA IMPRIMIR (por imprimir o imprimiéndose, de cirugías vivas). Suma en el
  // mismo número de «Cirugías»: es otra tarea que me espera ahí dentro.
  const { data: cirugiasGuias } = useQuery({
    queryKey: ['cirugias-guias-mias'],
    queryFn: () => api.get('/cirugias/guias/mias').then(r => r.data).catch(() => ({ pendientes: 0 })),
    refetchInterval: 60000,
    enabled: perms?.cirugias === true,
  })
  // Documentos del TABLÓN que me quedan pendientes → badge rojo en "Inicio", que es donde están desde el
  // 11-sep. Un documento que exige FIRMA cuenta hasta que yo lo firme (abrirlo no lo apaga); el resto,
  // hasta abrirlo. Lo calcula el servidor con semántica de destinatario (los privados de otros no suman).
  // MENOS AL QUIOSCO DE FICHAR: /fichar cuelga de este Layout, el servidor le contesta 403 al tablón y
  // esta consulta se repite cada minuto. Un tótem encendido todo el día dejaba un 403 por minuto en su
  // consola y en el log del servidor, para siempre, por un badge que esa cuenta nunca puede ver.
  const { data: tablonPend } = useQuery({
    queryKey: ['tablon-pendientes'],
    queryFn: () => api.get('/tablon/contador').then(r => r.data).catch(() => ({ pendientes: 0 })),
    refetchInterval: 60000,
    enabled: user?.rol !== 'control_horario',
  })
  // Casos que un profesional de fuera ha mandado y siguen sin aceptar, más sus mensajes sin leer →
  // badge en "Referidores". Es lo único de la aplicación donde hay ALGUIEN DE FUERA esperando
  // respuesta: sin badge, un caso que entra a las nueve se queda ahí hasta que alguien se acuerda de
  // abrir la pantalla. `enabled` por permiso (no por rol): si el admin se lo concede a una persona
  // concreta, esa persona tiene que ver su badge.
  // MIS CONSENTIMIENTOS: los pendientes de rellenar de SUS agendas. El servidor lo cuenta con la MISMA funcion
  // que el WhatsApp de «tienes N pendientes», asi que la chapa y el mensaje dicen el mismo numero. Solo se
  // pregunta a quien tiene agenda: a los demas siempre les contestaria 0.
  const { data: misConsPend } = useQuery({
    queryKey: ['mis-consentimientos-resumen'],
    queryFn: () => api.get('/mis-consentimientos/resumen').then(r => r.data).catch(() => ({ pendientes: 0 })),
    refetchInterval: 60000,
    enabled: _agendas.length > 0,
  })
  // SOLICITUDES DE CIERRE DE AGENDA pendientes de aprobar. Hasta hoy NADIE avisaba: la solicitud se
  // guardaba y se quedaba esperando en una pestaña de Ajustes que hay que ir a mirar a propósito. El
  // colaborador daba su día por cerrado y, si nadie entraba ahí, el día de la cita el paciente se
  // plantaba en la puerta. El servidor ya devolvía `pendientes` y ya acota lo que ve cada uno (un
  // colaborador, las suyas; administración, todas), así que aquí no hay que filtrar nada.
  const { data: agendaSolPend } = useQuery({
    queryKey: ['agenda-solicitudes-contador'],
    queryFn: () => api.get('/horarios/solicitudes').then(r => r.data).catch(() => ({ pendientes: 0 })),
    refetchInterval: 60000,
    enabled: user?.rol !== 'control_horario',
  })
  const { data: derivPend } = useQuery({
    queryKey: ['derivaciones-contador'],
    queryFn: () => api.get('/derivaciones/contador').then(r => r.data).catch(() => ({ total: 0 })),
    refetchInterval: 60000,
    enabled: perms?.derivaciones !== false,
  })
  // ASISTENCIAS EN BORRADOR. El doctor las dicta al salir de la cirugía y siguen ahí hasta que
  // alguien entra a repasarlas, ponerles precio y publicarlas — y a entrar no entra nadie sin un
  // número delante. La pantalla vive en el panel, así que aquí el badge cuelga de «Panel de
  // administración», que es la puerta por la que se llega.
  // `enabled` doble: solo si esa clínica tiene el módulo y solo para quien ve la entrada.
  const { data: asistPend } = useQuery({
    queryKey: ['asistencias-contador'],
    queryFn: () => api.get('/asistencias/contador').then(r => r.data).catch(() => ({ borradores: 0 })),
    refetchInterval: 60000,
    // Por PERMISO, no por rol: al panel entra tambien quien no es admin pero tiene concedida esa
    // seccion, y es justo el doctor que dicta las asistencias quien tiene que ver el numero.
    // B-144 · y solo donde existen: el servidor no la tiene apagada para esta clínica (services/asistenciasModulo).
    enabled: branding.modulo_asistencias !== false && permisosListos && Array.isArray(perms?._sistemas_apagados)
      && !perms._sistemas_apagados.includes('asistencias')
      && (esAdminPanel || perms?.asistencias === true),
  })
  // Módulos que el admin ha ocultado a cada rol (capa de visibilidad configurable).
  const { data: rolMods } = useQuery({
    queryKey: ['rol-modulos'],
    queryFn: () => api.get('/rol-modulos').then(r => r.data).catch(() => ({ ocultos: [] })),
    staleTime: 60000,
  })
  // ¿A este usuario le toca fichar? Si tiene desmarcado "Debe fichar", el módulo Fichar sobra.
  // Se pregunta al servidor (y no al `user` de localStorage) para que el cambio se note sin
  // tener que volver a entrar.
  const { data: ficharCfg } = useQuery({
    queryKey: ['fichar-modo'],
    queryFn: () => api.get('/control-horario/modo').then(r => r.data).catch(() => ({})),
    staleTime: 60000,
  })
  // ¿Esta clínica deja que cada uno gestione su agenda? Decide si la entrada "Mi agenda" se ve.
  // ── ORDEN DEL MENÚ, POR USUARIO ────────────────────────────────────────────────────────────
  // Cada uno usa la app de una forma: recepción vive en Agenda y WhatsApp, un doctor en su ficha y
  // el periodontograma, almacén en Pedidos. Un orden único obliga a todos a recorrer el menú de la
  // persona que lo decidió. Se guarda en las preferencias del usuario (cross-device, no en este
  // navegador): quien lo ordena en el ordenador de la clínica se lo encuentra igual en su móvil.
  const { data: prefsMenu } = useQuery({
    queryKey: ['auth-preferencias'],
    queryFn: () => api.get('/auth/preferencias').then(r => r.data).catch(() => ({})),
    staleTime: 600000,
  })
  // ESPEJO DE "AL ENTRAR, LLÉVAME A…". La preferencia vive en el servidor (viaja entre
  // ordenadores), pero quien decide el aterrizaje lo hace en mitad de un redirect y ahí no se puede
  // esperar a una petición. Layout está montado en todas las pantallas, así que es el sitio natural
  // para dejar la última elección conocida en este navegador: la próxima vez se entra sin parpadeo.
  useEffect(() => {
    if (prefsMenu) guardarInicioPreferido(typeof prefsMenu.inicio_ruta === 'string' ? prefsMenu.inicio_ruta : null, user?.id)
  }, [prefsMenu?.inicio_ruta, user?.id])
  const [ordenando, setOrdenando] = useState(false)
  const [orden, setOrden] = useState(null)      // null = todavía el del servidor
  const arrastrando = useRef(null)
  const ordenGuardado = prefsMenu?.sidebar_orden
  // El orden vive por SECCIÓN: mover "Pedidos" a la cabecera del menú entero rompería los grupos, y
  // los grupos son lo que hace que un menú de treinta entradas se pueda leer.
  const ordenDe = (section, tos) => {
    const guardado = (orden || ordenGuardado || {})[section]
    if (!Array.isArray(guardado)) return tos
    // Lo guardado manda, pero lo que NO esté (una página nueva, o una a la que antes no tenía
    // acceso) va al final en vez de desaparecer: un menú que esconde opciones sin decirlo es peor
    // que uno mal ordenado.
    const enOrden = guardado.filter(t => tos.includes(t))
    return [...enOrden, ...tos.filter(t => !enOrden.includes(t))]
  }
  const guardarOrden = async (next) => {
    setOrden(next)
    try { await api.patch('/auth/preferencias', { sidebar_orden: next }) } catch (_) { /* se reintenta al siguiente arrastre */ }
  }
  const soltar = (section, tos, destino) => {
    const origen = arrastrando.current
    arrastrando.current = null
    if (!origen || origen === destino) return
    const lista = ordenDe(section, tos).slice()
    const i = lista.indexOf(origen); const j = lista.indexOf(destino)
    if (i < 0 || j < 0) return
    lista.splice(j, 0, ...lista.splice(i, 1))
    guardarOrden({ ...(orden || ordenGuardado || {}), [section]: lista })
  }

  const { data: capAgenda } = useQuery({
    queryKey: ['agenda-capacidad'],
    queryFn: () => api.get('/horarios/capacidad').then(r => r.data).catch(() => ({})),
    staleTime: 300000,
  })
  // Entrada al PANEL DE ADMINISTRACIÓN para NO-admins: si el admin le ha concedido alguna sección del
  // panel (Informes, Caja, Catálogo…), se enseña el botón y se le lleva a su PRIMERA sección permitida
  // — la landing /administracion es solo-admin y lo rebotaría. El admin/superadmin entra a /administracion
  // como siempre. AdminLayout ya filtra su propio menú por permiso, así que dentro solo verá lo suyo.
  //
  // Y SE PREGUNTA CON `puedeSeccionAdmin`, NO CON EL BOOLEANO. Aquí ponía `perms?.[s.perm] === true`, y
  // esa línea dejaba SIN NINGUNA PUERTA al gestor con acceso de consulta: quien tiene solo
  // `contabilidad_lectura` tiene `perms.contabilidad === false` A PROPÓSITO (el servidor lo dice con
  // esas palabras en services/permisos.js), así que `entradaAdmin` salía null, el botón «Panel de
  // administración» se escondía, y /contabilidad —que solo vive dentro del panel— no tenía ni un
  // enlace por ninguna parte: se llegaba tecleando la URL. La clínica concedía el permiso a su
  // asesoría, la asesoría entraba y no había contabilidad. El acceso RESUELTO lo manda el servidor en
  // `perms._accesos` ('completo' | 'lectura' | 'ninguno') y quien lo sabe leer es
  // services/permisosAdmin.js: la misma pieza que usan AdminLayout (su menú) y App.jsx (sus rutas).
  // Deducirlo aquí otra vez sería tener la misma decisión escrita en tres sitios, que es exactamente
  // como se llegó a esto.
  // Y «¿ES ADMINISTRADORA DE ESTA CLINICA?» SE PREGUNTA, NO SE COPIA. Aqui ponia
  // `user?.rol === 'admin' || user?.es_superadmin === true`, la misma frase que el servidor acaba
  // de quitar de 32 routers. Medido por HTTP: a una cuenta de plataforma con CERO secciones
  // `/api/administracion/resumen` y `/api/admin/dashboard` le contestan el MISMO 403 que a su
  // companera de rol, y esta linea le pintaba igualmente «Panel de administracion» — una puerta
  // con cero secciones detras. La decision la manda el servidor ya resuelta (`_es_admin`) y aqui
  // solo se lee: services/permisosDelServidor.
  const ctxAdmin = { esAdmin: esAdminPanel, esSuperadmin: user?.es_superadmin === true, perms }
  const seccionesAdminPermitidas = esAdminPanel
    ? ['/administracion']
    : Object.entries(ADMIN_SECCIONES).filter(([ruta, s]) => s.perm && puedeSeccionAdmin(ruta, ctxAdmin)).map(([ruta]) => ruta)
  const entradaAdmin = esAdminPanel ? '/administracion' : (seccionesAdminPermitidas[0] || null)
  // Override de menú por rol: { ruta: visible }. mostrar=true, ocultar=false; sin entrada = default.
  const overrideRol = {}
  ;(rolMods?.overrides || []).filter(o => o.rol === user?.rol).forEach(o => { overrideRol[o.ruta] = o.visible })
  // ¿Entra esta persona por la PUERTA ACOTADA del almacén? (B-102) Es encargada de una lista de la
  // compra y no tiene el módulo: entonces esa lista —y nada más del almacén— es suya. La decisión la
  // manda el servidor en `_compras_encargado` (la misma función que abre la puerta en
  // services/accesoAcotado); aquí solo se lee.
  const soloSuListaCompra = perms?._compras_encargado === true && perms?.stock !== true
  // LA ENTRADA DE UN SISTEMA QUE EL SERVIDOR ENCIENDE CLÍNICA A CLÍNICA (la Nube, P7; `sistema` en la entrada). Se pinta
  // SOLO cuando la respuesta de los permisos ya ha llegado y dice que ese sistema está encendido en esta clínica
  // (`_sistemas_apagados`, backend/services/permisos · aplicarSistemasApagados). Mientras no ha llegado, no sale: por rol
  // aparecería y se iría en cada F5 en todas las clínicas que no la tienen. Sin el dato, tampoco (lado cerrado).
  const sistemaEncendido = (i) => !!i.sistema && permisosListos && Array.isArray(perms?._sistemas_apagados)
    && !perms._sistemas_apagados.includes(i.sistema)
  // ¿Es visible este módulo? Primero lo que no es un permiso (soloEmail/soloSuperadmin, el tótem de
  // fichar, la autonomía de agenda) y después LA REGLA, que es una y vive en
  // services/visibilidadModulo: si el módulo tiene clave, manda el permiso.
  const moduloVisible = (i) => {
    // t279 · LA ENTRADA DE UNA FUNCIÓN EN DESARROLLO («funcion: 'clave'» en la entrada, shared/funciones.json): solo con ella
    // encendida para esta clínica (la lista llega con /api/config-cliente; mientras no, apagada). Va lo primero: ni un permiso
    // ni un «Mostrar» de Accesos por rol la abren.
    if (i.funcion && !funcionEncendida(i.funcion)) return false
    // B-176 · LA EDICIÓN LOCAL (Biango instalado en la clínica, sin internet): lo que no está en ella no sale, ni por permiso ni
    // por «Mostrar». En la nube `fueraDeLaEdicion` dice siempre que no (utils/edicionLocalEstado).
    if (fueraDeLaEdicion(i.to)) return false
    // (P7) La entrada de un sistema que el servidor enciende clínica a clínica (la Nube): solo cuando ya se sabe que aquí
    // está encendido (`sistemaEncendido`, arriba). Va lo primero: ni un permiso ni un «Mostrar» de Accesos por rol la abren.
    if (i.sistema && !sistemaEncendido(i)) return false
    // LA MIGRACIÓN ABIERTA PARA TODO EL EQUIPO (1-oct-2026): Biango la abre en la Licencia de la clínica y elige quién la ve.
    // Con «todo el equipo», el servidor lo dice en `_migracion_para_el_equipo` y la entrada sale a cualquiera, no solo a la administración.
    if (i.to === '/migracion' && perms?._migracion_para_el_equipo === true) return true
    // Panel de administración: visible para el admin y para el no-admin con alguna sección concedida.
    if (i.to === '/administracion') return !!entradaAdmin
    // La puerta acotada ABRE su entrada, aunque su rol no tenga el módulo: es lo único que ve de él.
    if (i.abreConEncargoCompras && soloSuListaCompra) return true
    if (i.soloEmail && (user?.email || '').toLowerCase() !== i.soloEmail.toLowerCase()) return false
    if (i.soloSuperadmin && user?.es_superadmin !== true) return false
    // El quiosco (rol control_horario) es el tótem de la clínica: ahí sí se ficha siempre.
    if (i.to === '/fichar' && ficharCfg?.debe_fichar === false && user?.rol !== 'control_horario') return false
    // Sin autonomía activada no hay nada que hacer ahí: se esconde en vez de llevar a una pantalla
    // que solo dice "pídeselo a administración".
    if (i.to === '/mi-agenda' && !capAgenda?.autonomia_activa) return false
    // MIS CONSENTIMIENTOS: sale a quien tiene agenda de doctor (con las agendas del login mientras llegan las
    // frescas, para que no aparezca y desaparezca en cada F5) y a quien tiene la clave «ver los consentimientos
    // de otros doctores».
    //
    // B-575 (17-sep-2026) · ANTES SE LE ESCONDIA AL ADMINISTRADOR SIN AGENDA, con `!esAdminPanel`, para que un
    // despliegue no le pusiera un boton nuevo. El dueño lo pidio al reves: «solo los admin ven todos», y desde
    // esta tanda ahi hay algo que SOLO puede hacer administracion —ver «Todos los doctores» y saber que
    // consentimientos estan esperando a cada uno—. Esconderle la entrada al unico que puede usarla era el
    // mundo al reves. Es la UNICA diferencia de menu que trae la tanda y esta declarada como tal en
    // routes/permisos-el-permiso-manda.test.js §6 (`GANADAS_A_PROPOSITO`), que es quien vigila que un
    // despliegue no reparta accesos sin que nadie lo decida.
    // MIENTRAS LOS PERMISOS NO HAN LLEGADO decide el ROL, igual que el resto del menu: la clave es de
    // fabrica del administrador (`rolesDefault: R_ADMIN`, backend/services/permisos.js), asi que pintarlo
    // por rol da el mismo menu que dara la respuesta. Si no, el boton aparece de la nada en cada F5 —y el
    // guard de ruta usa la misma decision, o sea que es una persona expulsada de su pantalla—.
    if (i.to === '/mis-consentimientos' && !(_agendas.length > 0
      || (permisosListos ? perms?.ver_consentimientos_de_otros === true : (rolMenu === 'admin' || user?.rol === 'admin')))) return false
    // UN PERMISO EXPLÍCITO MANDA (concedido o denegado a esta persona o a su rol); si nadie lo ha
    // tocado, decide la lista `roles` de arriba, igual que hasta hoy. La regla NO se escribe aquí:
    // vive en services/visibilidadModulo, porque hooks/useModuloVisible hace la misma pregunta para
    // las pestañas de Seguimiento y para «Al entrar, llévame a…» de Mi perfil, y PermRoute la hace
    // para la puerta de la ruta. Tres copias de una decisión acaban diciendo cosas distintas delante
    // del usuario — era literalmente este bug.
    return decideVisibilidad({
      ruta: i.to, roles: i.roles, clave: MODULO_CLAVE[i.to],
      rolUsuario: user?.rol, rolBase: rolMenu, perms, overrideRol,
    })
  }
  // Guard suave: si el usuario llega (por URL directa) a un módulo no visible para él, lo mandamos a
  // la primera pantalla visible. Solo afecta a rutas que SON un módulo del menú (no sub-páginas).
  useEffect(() => {
    if (esAdminPanel) return
    // Esperar a que cargue el override del admin: en una recarga completa (p. ej. "abrir ficha"
    // desde el modal de cita) rolMods aún es undefined y sin esta espera se expulsaba por el
    // default del rol a usuarios a los que el admin SÍ les mostró el módulo.
    if (rolMods === undefined) return
    const mod = navItems.flatMap(s => s.items).find(m => m.to === location.pathname)
    // Y A LOS PERMISOS, POR LO MISMO, Y AHORA EN TODOS LOS MÓDULOS CON CLAVE (antes solo en los tres
    // marcados `porPermiso`): quien tiene un módulo CONCEDIDO a mano solo se distingue cuando ha
    // llegado la respuesta (`_explicitas`), y hasta entonces la regla lo decide por su rol — que es
    // justamente el rol que NO lo tiene. Sin esta espera, la recepcionista con «Almacén» concedido
    // pulsaba F5 dentro de /stock y la echaba de su propia pantalla: el fallo molesto convertido en
    // uno que para la clínica. `permisosListos` es la marca de "ya ha llegado la respuesta"
    // (`_base_rol`, que viaja siempre en ella). Si la petición falla no se expulsa a nadie: el
    // servidor sigue contestando 403 por su cuenta, que es la barrera de verdad.
    if (mod && MODULO_CLAVE[mod.to] && !permisosListos) return
    if (mod && !moduloVisible(mod)) {
      const primera = navItems.flatMap(s => s.items).find(m => !m.oculto && moduloVisible(m) && !m.soloSuperadmin && (!m.soloEmail || (user?.email || '').toLowerCase() === m.soloEmail.toLowerCase()))
      navigate(primera?.to || '/perfil', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, rolMods, perms, permisosListos, ficharCfg])
  // Pacientes de la lista de urgencias que quedan por repasar. El badge NO se va hasta que la lista
  // está limpia (aunque el modal se haya pospuesto): es el recordatorio permanente de que queda algo
  // por decidir. Cada usuario ve SU número (un doctor, el de sus pacientes) — lo acota el servidor.
  const { data: repasoUrg } = useQuery({
    queryKey: ['urgencias-repaso-estado'],
    queryFn: () => api.get('/urgencias/repaso/estado').then(r => r.data).catch(() => null),
    refetchInterval: 60000,
    // Solo se pregunta a quien tiene el módulo. Por PERMISO (no por una lista de roles repetida
    // aquí): si el admin se lo concede a una persona concreta, esa persona tiene que ver su badge; y
    // a quien se lo deniega deja de consultarse cada minuto para recibir un 403.
    enabled: perms?.urgencias === true,
  })
  const badges = {
    '/comunicacion': (parseInt(contadores.sin_leer) || 0) + (Number(labSinLeer.total) || 0),
    '/chat': chatNoLeidos,
    ...((misConsPend?.pendientes || 0) > 0 ? { '/mis-consentimientos': misConsPend.pendientes } : {}),
    // Y SOLO SI ESE ROL VE LOS DOCUMENTOS EN INICIO: un número que lleva a una pantalla donde no están
    // (la clínica se los ocultó a su rol) es un aviso sin salida.
    ...((Number(tablonPend?.pendientes) || 0) > 0 && moduloVisible({ to: '/tablon', roles: ROLES_DOCUMENTOS_TABLON })
      ? { '/inicio': Number(tablonPend.pendientes) } : {}),
    ...((repasoUrg?.pendientes || 0) > 0 ? { '/urgencias': repasoUrg.pendientes } : {}),
    // CIERRES DE AGENDA pendientes. Administración las resuelve en Ajustes; el colaborador solo ve
    // las suyas, y las suyas se miran en «Mi agenda». Va SUMADO a lo que ya cuelga de '/ajustes'
    // (bajas pendientes): asignarlo a la misma clave pisaría el otro número y uno de los dos avisos
    // desaparecería sin que nadie lo notara.
    ...(user?.rol === 'admin'
      ? (((contadores.bajas_pendientes || 0) + (agendaSolPend?.pendientes || 0)) > 0
        ? { '/ajustes': (contadores.bajas_pendientes || 0) + (agendaSolPend?.pendientes || 0) } : {})
      : ((agendaSolPend?.pendientes || 0) > 0 ? { '/mi-agenda': agendaSolPend.pendientes } : {})),
    // ALMACÉN. El badge colgaba de '/stock/lista-compra', que es una entrada OCULTA del menú (se
    // llega a ella por las pestañas del módulo), así que el render la filtraba y ese badge NO SE
    // PINTABA NUNCA: código muerto desde que se hizo. Va en '/stock', que es la entrada visible.
    // Y cuenta LO MÍO (`mias`) si soy responsable de compras; si no lo soy, el total de la clínica
    // como hasta ahora — a quien lleva el almacén le sigue interesando el número entero.
    // Y para el encargado sin almacén, el badge va en la entrada que SÍ ve: su lista.
    ...((stockContador.mias || 0) > 0 || (stockContador.pendientes || 0) > 0
      ? { [soloSuListaCompra ? '/stock/lista-compra' : '/stock']: (stockContador.mias || 0) > 0 ? stockContador.mias : stockContador.pendientes } : {}),
    // CIRUGÍAS: material de una cirugía viva que me toca comprar a mí, y (B-839) las guías quirúrgicas que me toca imprimir.
    ...(((cirugiasCompras?.pendientes || 0) + (cirugiasGuias?.pendientes || 0)) > 0
      ? { '/cirugias': (cirugiasCompras?.pendientes || 0) + (cirugiasGuias?.pendientes || 0) } : {}),
    // REFERIDORES: casos sin aceptar y mensajes del profesional que los mandó. La chapa cuelga de
    // la entrada del menú, que desde B-106 es '/referidores'.
    ...((derivPend?.total || 0) > 0 ? { '/referidores': derivPend.total } : {}),
    // ASISTENCIAS dictadas y sin repasar (la pantalla está dentro del panel).
    ...((asistPend?.borradores || 0) > 0 ? { '/administracion': asistPend.borradores } : {}),
  }
  // B-606 · EL NUMERO TAMBIEN CUANDO EL MENU NO SE VE. Los badges de arriba viven en las entradas del
  // menu lateral, y el menu lateral esta ESCONDIDO en el movil (y colapsado en escritorio si asi lo dejo
  // el usuario): un caso que entra a las nueve no lo anunciaba nada. La suma va en el boton de menu, que
  // es lo unico que se ve siempre; el CSS de abajo la enseña SOLO mientras el menu este oculto, para no
  // repetir el mismo numero dos veces cuando el menu esta a la vista.
  const totalBadges = Object.values(badges).reduce((s, n) => s + (Number(n) || 0), 0)

  useEffect(() => { setSidebarOpen(false) }, [location.pathname])
  // 4-oct-2026 · ?abrir=<id> (un botón de Primeros pasos, un enlace compartido): trae ese apartado a la vista y lo resalta,
  // esperando a que la pantalla lo pinte (utils/apartado). El desplegable o la pestaña se abren solos al leer la dirección.
  useEffect(() => alCambiarLaDireccion(), [location.pathname, location.search, location.hash])

  // SALIR RECARGA DE VERDAD, y no es un capricho. La aplicación es una sola página: `navigate()`
  // cambia de pantalla sin volver a pedir index.html, así que el navegador puede seguir ejecutando
  // el código de hace días aunque el servidor ya tenga otro. Y resulta que "cerrar sesión y entrar
  // de cero" es justo el gesto que la gente usa cuando algo va mal — el único que NUNCA traía el
  // arreglo. Con location.href, salir y volver a entrar trae también el código nuevo.
  // B-1013 · con subidas a la Nube en marcha se pregunta ANTES de salir (utils/nubeSubidasGlobal): si se sigue, se cancelan avisando al servidor, todavía con esta sesión; si no, no se sale.
  const handleLogout = async () => { if (!(await salirConSubidas('cierras sesión'))) return; logout(); window.location.href = '/login' }
  // ── t93 · CAMBIAR DE CLÍNICA SIN CERRAR SESIÓN ────────────────────────────────────────────────
  // Las clínicas salen de la sesión de verdad (`realUser`, no la previsualizada): las que la contraseña abrió al entrar.
  // Al elegir una, el servidor abre una sesión NUEVA allí y cierra esta (backend/services/cambioDeClinica.js); aquí se
  // entra con ella COMO EN UN INICIO DE SESIÓN (login() borra todo lo de la sesión anterior: services/sesion) y se
  // RECARGA la aplicación entera, igual que al salir: ni una consulta, ni una pantalla, ni un dato en memoria de la
  // clínica de antes. Con subidas a la Nube en marcha se pregunta antes, como al cerrar sesión.
  const otrasClinicas = clinicasParaCambiar(realUser || user)
  const [cambiandoA, setCambiandoA] = useState(null)
  const [retoCambio, setRetoCambio] = useState(null)
  const entrarEnLaOtraClinica = (data) => {
    olvidarLoDeLaAnterior()
    login(data.agente, data.token)
    window.location.href = data.agente?.rol === 'control_horario' ? '/fichar' : '/'
  }
  const cambiarDeClinica = async (c) => {
    if (!c || cambiandoA != null) return
    if (!(await salirConSubidas(tMenu('components.Layout.cambias_de_clinica', 'cambias de clínica')))) return
    setCambiandoA(c.clinica_id)
    try {
      const r = await pedirCambioDeClinica(api, c.clinica_id)
      if (r.segundo_factor) { setRetoCambio({ datos: r.segundo_factor, clinica: c }); setCambiandoA(null); return }
      if (!r.token || !r.agente) throw new Error(tMenu('components.Layout.sin_sesion', 'sin sesión'))
      entrarEnLaOtraClinica(r)
    } catch (e) {
      setCambiandoA(null)
      await avisar({ titulo: tMenu('components.Layout.no_se_ha_podido_cambiar', 'No se ha podido cambiar de clínica'), mensaje: errorDelCambio(e), tipo: 'danger' })
    }
  }
  // Tras el código: la sesión de la que se sale se cierra con ESTA sesión (el servidor no la conoce en ese camino) y se
  // entra en la nueva. Si cerrarla falla, se entra igual: caduca sola.
  const trasElCodigoDelCambio = async (data) => {
    const sid = sidDelToken(localStorage.getItem('token'))
    if (sid) { try { await api.delete('/seguridad/sesiones/' + sid, { data: { motivo: 'cambio a otra clínica' } }) } catch (_) { /* caduca sola */ } }
    setRetoCambio(null)
    entrarEnLaOtraClinica(data)
  }
  const modalCambioDeClinica = retoCambio
    ? <SegundoFactorDelCambio datos={retoCambio.datos} clinica={retoCambio.clinica} onEntrar={trasElCodigoDelCambio} onVolver={() => setRetoCambio(null)} />
    : null
  const toast = useToast()
  // EL TECHO DE LA SESIÓN SE AVISA ANTES DE QUE LLEGUE. El token se renueva solo cada 45 min y eso
  // no se nota; lo que NO se renueva es el techo de 7 días desde que se tecleó la contraseña. Sin
  // este aviso, ese límite llegaba de golpe y en silencio: la siguiente petición devolvía 401 y el
  // interceptor mandaba al login, con lo que se estuviera escribiendo a medias. El evento lo emite
  // services/renovarSesion.js, que no tiene contexto para pintar nada.
  // El objeto `toast` se rehace en cada render (el contexto no lo memoriza), así que como dependencia
  // del efecto haría que el oyente se quitara y se pusiera continuamente. Por eso va por referencia.
  const toastRef = useRef(toast)
  toastRef.current = toast
  useEffect(() => {
    const alAvisar = (e) => {
      // El TEXTO vive en utils/avisoSesion: metido aqui dentro no se podia comprobar, y se quedo
      // diciendo algo que ni el dueño entendia (no decia por que pasaba ni que habia que hacer).
      toastRef.current.info(avisoDeSesion(e?.detail?.restanMin),
      15000)   // el segundo argumento del toast son MILISEGUNDOS, no un objeto de opciones
    }
    window.addEventListener('sesion-por-caducar', alAvisar)
    return () => window.removeEventListener('sesion-por-caducar', alAvisar)
  }, [])
  // Switch trabajador → paciente: si el trabajador tiene ficha de paciente con su email, abre SU
  // portal con sesión propia (portal_token, separada de la de trabajo — ninguna pisa a la otra).
  //
  // PIDE LA CONTRASEÑA DEL PORTAL (6-sep-2026), y no es un trámite: hasta ahora el servidor abría la
  // cuenta del portal que tuviera ESE CORREO, y el correo del perfil se lo pone cada uno. Con eso se
  // entraba en la identidad de otra persona (y se le colgaba dentro un vínculo que desviaba su
  // código de «he olvidado mi contraseña»). La contraseña del portal es lo único que demuestra que
  // esa cuenta es tuya, así que se pide aquí. Si aún no tiene portal, el servidor contesta
  // `necesita_invitacion` y se le dice qué pedir en su clínica en vez de fabricarle una cuenta.
  const abrirMiPortal = async (passwordPortal) => {
    try {
      const r = await api.post('/auth/portal-exchange', passwordPortal ? { password_portal: passwordPortal } : {})
      localStorage.setItem('portal_token', r.data.token)
      navigate('/portal')
    } catch (e) {
      const d = e.response?.data || {}
      if (d.necesita_password_portal) {
        const pw = await pedirTexto({
          titulo: tMenu('components.Layout.tu_portal_de_paciente', 'Tu portal de paciente'),
          mensaje: d.error || tMenu('components.Layout.escribe_la_contrasena_de_tu', 'Escribe la contraseña de tu portal de paciente para entrar en él.'),
          detalle: tMenu('components.Layout.es_la_contrasena_con_la', 'Es la contraseña con la que entras como PACIENTE, no la de tu cuenta de trabajo.'),
          tipoCampo: 'password',
          textoConfirmar: tMenu('components.Layout.entrar', 'Entrar'),
        })
        if (pw) await abrirMiPortal(pw)
        return
      }
      if (d.necesita_invitacion) {
        await avisar({
          titulo: tMenu('components.Layout.todavia_no_tienes_portal_de', 'Todavía no tienes portal de paciente'),
          mensaje: d.error,
          detalle: tMenu('components.Layout.la_invitacion_se_envia_desde', 'La invitación se envía desde tu ficha de paciente (Pacientes → tu ficha → Portal). La activas con tu fecha de nacimiento y eliges tu contraseña.'),
        })
        return
      }
      toast.error(d.error || tMenu('components.Layout.no_se_pudo_abrir_tu', 'No se pudo abrir tu portal de paciente'))
    }
  }

  // ════════════════════════════════════════════════════════════════════════════════════════════
  //  B-635 · «MI PORTAL DE REFERIDORES» — ESTE BLOQUE ES DE LA TANDA DE REFERIDORES
  //  (el resto del fichero lo está tocando la tanda de soporte, sin subir: aquí solo se AÑADE)
  // ════════════════════════════════════════════════════════════════════════════════════════════
  //
  // Un mismo correo puede ser trabajador de la clínica Y profesional que le deriva pacientes. Hasta
  // ahora, para entrar en su portal de referidores tenía que salir de la aplicación y volver a
  // entrar por /referidor/login. Esto es la misma puerta que ya existe para el portal de paciente.
  //
  // PIDE LA CONTRASEÑA DEL PORTAL, por el mismo motivo que aquella: el correo del perfil se lo pone
  // cada uno, así que abrir esa identidad sin la credencial que la abre sería suplantación.
  // Y va POR CLÍNICA: la cuenta de profesional es una por clínica y cada una tiene su propia
  // contraseña — el servidor no busca nunca solo por correo.
  //
  // Qué puertas se pintan lo dice el servidor (`GET /auth/mis-portales`), no una suposición: antes
  // «Mi portal de paciente» se le pintaba a TODO el mundo y a la mayoría le contestaba un 404.
  //
  // TRES ESTADOS, NO DOS: `null` es «todavía no se sabe». Con `{paciente:false}` de arranque, una
  // puerta que hoy existe PARA TODO EL MUNDO —«Mi portal de paciente»— desaparecía en cada apertura
  // del menú hasta que contestara la petición, y PARA SIEMPRE si `/auth/mis-portales` tardaba,
  // fallaba o la red se cortaba (su `.catch` no cambia nada). Mientras no se sepa se pinta lo de
  // siempre —que es lo que no puede empeorar— y se esconde solo la de referidores, que es nueva.
  // Y SE PIDE UNA VEZ, AL MONTAR, no en cada apertura: así el menú no cambia de altura delante de
  // quien lo está mirando.
  const [portales, setPortales] = useState(null)
  useEffect(() => {
    let vivo = true
    api.get('/auth/mis-portales')
      .then(r => { if (vivo) setPortales({ paciente: !!r.data?.paciente, referidor: !!r.data?.referidor }) })
      .catch(() => { /* sin respuesta se queda en «no se sabe»: la puerta de paciente sigue como hasta hoy */ })
    return () => { vivo = false }
  }, [])

  const abrirMiPortalReferidor = async (passwordPortal) => {
    try {
      const r = await api.post('/auth/referidor-exchange', passwordPortal ? { password_portal: passwordPortal } : {})
      // EL TOKEN LO GUARDA SU DUEÑO (services/sesionReferidor), nunca `localStorage` a pelo:
      // `services/apiReferidor` lee PRIMERO `sessionStorage`, así que un token viejo en esta pestaña
      // taparía el nuevo y se entraría en la sesión anterior.
      const { guardar } = await import('../services/sesionReferidor')
      guardar(r.data.token, true)
      navigate('/referidor')
    } catch (e) {
      const d = e.response?.data || {}
      if (d.necesita_password_portal) {
        const pw = await pedirTexto({
          titulo: tMenu('components.Layout.tu_portal_de_referidores', 'Tu portal de referidores'),
          mensaje: d.error || tMenu('components.Layout.escribe_la_contrasena_de_tu_2', 'Escribe la contraseña de tu portal de profesionales para entrar en él.'),
          detalle: tMenu('components.Layout.es_la_contrasena_con_la_2', 'Es la contraseña con la que entras como profesional que deriva, no la de tu cuenta de trabajo.'),
          tipoCampo: 'password',
          textoConfirmar: tMenu('components.Layout.entrar', 'Entrar'),
          // t251 (15-oct-2026) · el dueño: «que haya un botón para ver la contraseña y restablecerla si no me acuerdo». El ojo lo
          // pone el diálogo; esto lleva a la entrada del portal a poner una nueva con un código por correo (el camino que ya
          // había). El correo viaja en el estado de la navegación, no en la dirección.
          textoOlvido: tMenu('components.Layout.no_la_recuerdas', '¿No la recuerdas?'),
          alOlvidar: () => navigate('/referidor/login?olvidada=1', { state: { email: user?.email || '' } }),
        })
        if (pw) await abrirMiPortalReferidor(pw)
        return
      }
      if (d.necesita_invitacion) {
        await avisar({
          titulo: tMenu('components.Layout.todavia_no_tienes_portal_de_2', 'Todavía no tienes portal de referidores'),
          mensaje: d.error,
          detalle: tMenu('components.Layout.el_acceso_se_envia_desde', 'El acceso se envía desde Referidores, pestaña «Catálogo de referidores», en el perfil del profesional.'),
        })
        return
      }
      toast.error(d.error || tMenu('components.Layout.no_se_pudo_abrir_tu_2', 'No se pudo abrir tu portal de referidores'))
    }
  }

  // ¿Estamos dentro del chat? El botón de la cabecera cambia de "entrar" a "salir".
  const enChat = location.pathname === '/chat' || location.pathname.startsWith('/chat/')
  // B-160 · lo que va delante del título de la pestaña: lo no leído del chat, si esta persona lo ve.
  // B-830 · ¿el chat está a la vista de esta persona? El mismo criterio que su botón de la cabecera, para el título y para
  // los avisos flotantes (ChatNotifFlotante): sin chat, ni número ni avisos.
  const chatALaVista = moduloVisible({ to: '/chat', roles: (navItems.flatMap(s => s.items).find(m => m.to === '/chat') || {}).roles })
  // B-875 · EL BUSCA: avisar a alguien del equipo de que le buscas (vibra hasta que contesta). Va con el chat de equipo:
  // lo ve quien ve el chat, porque lo que se dice va a ese chat. Aquí solo se lanza el evento: el panel vive montado en
  // la app (components/busca/BuscaGlobal), y la cabecera no carga nada más.
  const abrirBusca = () => { try { window.dispatchEvent(new CustomEvent('biango-abrir-busca')) } catch (_) { } }
  const chatEnTitulo = chatALaVista ? chatNoLeidos : 0
  // Dónde estábamos antes de entrar: en una ref (no en estado) porque cambiarla no tiene que
  // repintar nada, solo recordar el sitio al que volver.
  const volverDeChat = useRef(null)

  // t524 · y «Pacientes», con el vocabulario de su tipo («Clientes» en una estética; en la dental, como siempre).
  const pageName = (location.pathname === '/laboratorio' ? tMenu('components.Layout.laboratorio', 'Laboratorio') : location.pathname === '/pacientes' ? tMenu('components.Layout.pacientes', 'Pacientes') : tDato(navItems.flatMap(s => s.items).find(i => i.to === location.pathname)?.label)) || branding.nombre
  // Título de la pestaña del navegador: "Clínica · Sección" (p.ej. "Clínica Dental Esteve · Agenda")
  // para distinguir pestañas y ver de un vistazo dónde estás.
  useEffect(() => {
    const secc = navItems.flatMap(s => s.items).find(i => i.to === location.pathname)?.label
      // Las pantallas que no estan en el menu (la ficha de un paciente, sobre todo) tambien tienen
      // que decir donde estas: sin esto la pestana se quedaba solo con el nombre de la clinica,
      // que es justo lo que se repite en todas.
      || (/^\/pacientes\/\d/.test(location.pathname) ? tMenu('components.Layout.paciente', 'Paciente')
        : /^\/(comunicacion|whatsapp)/.test(location.pathname) ? tMenu('components.Layout.menu_comunicacion', 'Comunicación')
        : /^\/chat/.test(location.pathname) ? tMenu('components.Layout.chat', 'Chat') : null)
    // La pestaña tambien es del producto: index.html abre poniendo "Biango" y esto lo sobreescribia
    // con el nombre de la clinica en cuanto cargaba React, asi que la marca duraba medio segundo.
    document.title = tituloPestana(MARCA_NOMBRE, tDato(secc))   // B-2259 · en el idioma de quien mira (en castellano, igual)
    // B-160 · «NO ES FÁCIL SABER CUÁNDO ESCRIBEN». Trabajando en otra pestaña, lo único que se ve de
    // la aplicación es su título: los mensajes del chat sin leer van delante, «(3) Biango · Agenda»,
    // como en cualquier chat. Solo a quien ve el chat (el mismo criterio que su botón).
    if (chatEnTitulo > 0) document.title = '(' + (chatEnTitulo > 99 ? '99+' : chatEnTitulo) + ') ' + document.title
  }, [location.pathname, chatEnTitulo])
  // P1: en móvil, iniciales de la clínica (p.ej. "Clínica Dental Esteve" → "CDE") para no ocupar tanto.

  // Botón de perfil (avatar + desplegable). Reutilizado por la cabecera normal y la barra del kiosko.
  // t341 · el asistente LucIA del equipo, en el menú de la foto (provisional, hasta su botón flotante): solo con su interruptor.
  const asistenteLuciaEnMenu = user?.rol !== 'control_horario' && funcionEncendida('asistente-lucia')
  // t605 · «Idioma» en el menú de la foto, solo con la función «idiomas» (como el asistente: fuera de la rama del menú).
  const idiomaEnMenu = funcionEncendida('idiomas')
  const perfilMenu = (
    <div ref={perfilRef} style={{ position: 'relative' }}>
      {/* El avatar del propio usuario lleva el color de SU clínica, no el azul del producto. Estaba
          escrito a mano y era el punto más visible de la pantalla: en una clínica magenta, la bolita
          de arriba a la derecha seguía siendo azul. El degradado sale de los tokens (claro → base) y
          la letra de dentro de --sobre-primary, que es el que garantiza que se lea encima. */}
      {/* El punto ambar de "tienes algo que firmar". Va aqui —y no solo en Mi perfil— porque esta
          esquina es la unica que ve TODO el mundo en TODAS las pantallas; `perfilMenu` se usa dos
          veces (cabecera normal y barra del kiosko de Modo doctor), asi que con esto sale en las dos. */}
      <AvatarPorFirmar>
        <div onClick={() => setPerfilOpen(!perfilOpen)} title={tMenu('components.Layout.mi_perfil', 'Mi perfil')} style={{
          width: '32px', height: '32px', borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--primary-light), var(--primary))',
          color: 'var(--sobre-primary)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '13px', fontWeight: '700',
          cursor: 'pointer', border: '2px solid transparent', overflow: 'hidden',
        }}>
          {user?.avatar
            ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            : user?.nombre?.[0]?.toUpperCase()}
        </div>
      </AvatarPorFirmar>
      {perfilOpen && (
        // NUNCA MÁS ALTO QUE LA PANTALLA (8-oct-2026): el contenedor de la app no se desplaza, así que lo que el menú tenía
        // por debajo del borde (en el móvil, con «Contactar con Biango» abierto: las insignias, la ayuda, Cerrar sesión y la
        // versión) no se podía ver. Ahora se desplaza por dentro. En el móvil, además, más ancho: a 200 px lo desplegado
        // salía apretado (utils/contactoBiango.jsx, que lo busca por `data-menu-perfil` para bajar hasta lo abierto).
        // t302 · y encima de lo que flota (el aviso de «Hueco antes», el del chat): su capa es la de los desplegables abiertos
        // (index.css, «LAS CAPAS DE LA APLICACIÓN»), y su marca sube la barra entera a esa capa mientras está abierto.
        <div data-menu-perfil data-desplegable-cabecera="" style={{ position: 'absolute', right: 0, top: '42px', background: 'var(--surface)', borderRadius: '12px', border: '1px solid var(--border)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '200px', zIndex: 'var(--capa-desplegable-abierto)',
          maxHeight: 'calc(var(--alto-pantalla) - var(--sa-arriba) - var(--sa-abajo) - 66px)', overflowY: 'auto', overscrollBehavior: 'contain',
          ...(estrecho ? { width: 'min(320px, calc((100vw / var(--z, 1)) - 24px))' } : {}) }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-light)' }}>
            <div style={{ fontSize: '13px', fontWeight: '700' }}>{user?.nombre} {user?.apellidos}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{user?.email}</div>
            <div style={{ fontSize: '11px', color: 'var(--primary)', marginTop: '4px', fontWeight: '600' }}>{nombreRol(!user?._ver_como && user?.rol_real ? user.rol_real : user?.rol)}</div>
            {/* B-468 · el número de usuario de ESTA clínica, al pulsar el avatar (llega con el inicio de sesión). */}
            {user?.numero_en_clinica != null && (
              <div data-numero-usuario-menu style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{tMenu('components.Layout.n_de_usuario', 'Nº de usuario ')}<b style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{user.numero_en_clinica}</b></div>
            )}
          </div>
          <div style={{ padding: '6px' }}>
            <Link to="/perfil" onClick={() => setPerfilOpen(false)}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500' }}
              onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <Ico n="user" s={16} />{tMenu('components.Layout.mi_perfil_2', ' Mi perfil')}
            </Link>
            {/* t605 · B-2259 · «IDIOMA», AQUÍ, JUNTO A «MI PERFIL»: el dueño (31-oct) no encontraba dónde se cambia, porque la tarjeta
                vive en Mi perfil, Datos personales. Lleva a esa tarjeta y deja abierto su selector (pages/Perfil · ?abrir=idioma).
                Solo con la función «idiomas» encendida para esta clínica. */}
            {idiomaEnMenu && (
              <Link to="/perfil?tab=datos&abrir=idioma" data-menu-idioma="" onClick={() => setPerfilOpen(false)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500' }}
                onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <Ico n="flag" s={16} />{tMenu('components.Layout.idioma_menu', ' Idioma')}
              </Link>
            )}
            {/* t222 · LA FORMACIÓN, junto a «Mi perfil»: no va en el menú lateral (el dueño, 14-oct: «no es algo del día a día,
                pero sí debe ser accesible desde algún lugar fácilmente»). El quiosco de fichar no la ve. */}
            {user?.rol !== 'control_horario' && (
              <Link to="/formacion" data-menu-formacion="" onClick={() => setPerfilOpen(false)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500' }}
                onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <Ico n="birrete" s={16} />{tMenu('components.Layout.formacion', ' Formación')}
              </Link>
            )}
            {/* t341 · EL ASISTENTE LucIA DEL EQUIPO, provisional aquí hasta que llegue su botón flotante: solo con su interruptor. */}
            {asistenteLuciaEnMenu && (
              <button type="button" data-menu-asistente-lucia="" onClick={() => { setPerfilOpen(false); abrirAsistenteLucia() }}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <Ico n="bot" s={16} />{tMenu('components.Layout.asistente_lucia', ' Asistente LucIA')}
              </button>
            )}
            {/* t93 · las otras clínicas de esta persona (las que abrió su contraseña al entrar). */}
            <CambioDeClinica clinicas={otrasClinicas} ocupada={cambiandoA} onElegir={(c) => { setPerfilOpen(false); cambiarDeClinica(c) }} />
            {/* Junto a «Cambiar de clínica» va «Cambiar de usuario» (la pantalla del PIN, t95): los dos cambios de quién
                trabaja, juntos y debajo de «Mi perfil». */}
            <CambioDeUsuario onAntes={() => setPerfilOpen(false)} />
            {/* t530 · B-2268 · en un ordenador, en qué gabinete trabaja hoy (y cambiarlo). En el móvil no sale. */}
            <MiGabineteEnMenu onAntes={() => setPerfilOpen(false)} />
            {/* t478 · las otras cuentas abiertas en este dispositivo (la de Biango y la de la clínica) y «Añadir otra cuenta». */}
            <MenuDeCuentas onAntes={() => setPerfilOpen(false)} />
            {chatALaVista && (
              <button data-menu-busca onClick={() => { setPerfilOpen(false); abrirBusca() }}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <Ico n="megaphone" s={16} />{tMenu('components.Layout.busca', ' Busca')}
              </button>
            )}
            {/* B-050 · en el móvil, lo que no cabe en la cabecera. t271b · y en la tablet, cuando la medida dice que no cabe. */}
            {modosAlMenu && puedeModoDoctor && (
              <button data-menu-movil="modo-doctor" onClick={async () => {
                  setPerfilOpen(false)
                  if (modoDoctorSinAgenda) {
                    await avisar({
                      titulo: tMenu('components.Layout.falta_asignarle_su_agenda', 'Falta asignarle su agenda'),
                      mensaje: tMenu('components.Layout.este_usuario_tiene_permiso_de', 'Este usuario tiene permiso de Modo doctor, pero no tiene ninguna agenda asignada, así que no habría citas que mostrar.'),
                      detalle: tMenu('components.Layout.un_administrador_puede_asignarsela_en', 'Un administrador puede asignársela en Administración → Usuarios, en el apartado de agendas de ese usuario. En cuanto se guarde, el botón funciona sin necesidad de volver a entrar.'),
                    })
                    return
                  }
                  navigate('/doctor')
                }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <Ico n="calendarCheck" s={16} />{tMenu('components.Layout.modo_doctor', ' Modo doctor')}
              </button>
            )}
            {modosAlMenu && puedeModoAuxiliar && (
              <button data-menu-movil="modo-auxiliar" onClick={() => { setPerfilOpen(false); navigate('/auxiliar') }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <Ico n="users" s={16} />{tMenu('components.Layout.modo_auxiliar', ' Modo auxiliar')}
              </button>
            )}
            {temaAlMenu && (
              <button data-menu-movil="tema" onClick={() => { setPerfilOpen(false); toggleTema() }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <Ico n={dark ? 'sun' : 'moon'} s={16} /> {dark ? tMenu('components.Layout.modo_claro', 'Modo claro') : tMenu('components.Layout.modo_oscuro', 'Modo oscuro')}
              </button>
            )}
            {/* t271b · el reloj, cuando no cabe en la barra (lo que está en marcha sale en la franja de debajo). */}
            {relojAlMenu && (
              <button data-menu-movil="reloj" onClick={() => { setPerfilOpen(false); abrirReloj() }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <Ico n="clockCheck" s={16} />{tMenu('components.Layout.reloj', ' Reloj')}
              </button>
            )}
            {/* t578 · el «Cómo se hace» de la pantalla, cuando no cabe en la barra (nivel 7): abre lo mismo que el botón. */}
            {comoAlMenu && user?.rol !== 'control_horario' && <ComoSeHaceEnMenu ruta={location.pathname} busqueda={location.search} onAntes={() => setPerfilOpen(false)} />}
            {/* El «?» de la cabecera, en el móvil (t271b: y cuando no cabe): se despliega aquí mismo. */}
            {ayudaAlMenu && <ContactoBiango enMenu />}
            {/* B-635 · las dos puertas se pintan SOLO si existen de verdad en esta clínica
                (`GET /auth/mis-portales`). La de paciente se pintaba a todo el mundo y a la mayoría
                le contestaba un 404: una puerta que no lleva a ningún sitio se lee como avería.
                MIENTRAS NO SE SEPA (`portales === null`) LA DE PACIENTE SE PINTA COMO HASTA HOY: es
                una puerta que ya existía para todo el mundo, y hacerla desaparecer en cada apertura
                del menú —y para siempre si la petición falla— es la única forma de EMPEORARLA. La de
                referidores es nueva: esa solo aparece cuando el servidor dice que sí. */}
            {(portales === null || portales.paciente) && (
              <button onClick={() => { setPerfilOpen(false); abrirMiPortal() }}
                title={tMenu('components.Layout.si_ademas_eres_paciente_de', 'Si además eres paciente de la clínica (ficha con tu email), abre tu portal')}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <Ico n="star" s={16} />{tMenu('components.Layout.mi_portal_de_paciente', ' Mi portal de paciente')}
              </button>
            )}
            {portales !== null && portales.referidor && (
              <button onClick={() => { setPerfilOpen(false); abrirMiPortalReferidor() }}
                title={tMenu('components.Layout.si_tambien_derivas_pacientes_a', 'Si también derivas pacientes a esta clínica, abre tu portal de referidores')}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <Ico n="fonendo" s={16} />{tMenu('components.Layout.mi_portal_de_referidores', ' Mi portal de referidores')}
              </button>
            )}
            {/* LA PUERTA DEL PANEL DE PLATAFORMA, Y POR QUE VIVE AQUI.
                El panel de Superadmin existe desde hace meses y dentro esta, entre otras cosas, el
                editor de los BLOQUES DE CONSENTIMIENTO OFICIALES: el texto legal que firman los
                pacientes, con su versionado y su vuelta atras. No habia forma de llegar salvo
                escribiendo la URL a mano. Una pantalla construida a la que no llega nadie es, para
                quien la necesita, una pantalla que no existe.
                SOLO PARA `es_superadmin`, y del usuario EFECTIVO: al previsualizar como otro rol,
                AuthContext lo pone a false a proposito, y aqui eso es lo correcto — quien esta
                viendo la aplicacion como recepcionista no puede tener delante la puerta que la
                recepcionista no ve. Enseñarsela a un admin normal solo consigue que pulse, se coma
                el 403 del servidor y de paso se entere de que ese panel existe. */}
            {user?.es_superadmin === true && (
              <Link to="/superadmin" onClick={() => setPerfilOpen(false)}
                title={tMenu('components.Layout.clinicas_licencias_y_la_documentacion', 'Clínicas, licencias y la documentación legal que firman los pacientes')}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500' }}
                onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <Ico n="settings" s={16} />{tMenu('components.Layout.superadmin', ' Superadmin')}
              </Link>
            )}
            <button onClick={() => { setPerfilOpen(false); handleLogout() }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: '#e53e3e', fontWeight: '500', background: 'none', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.background='#fff5f5'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <Ico n="logout" s={16} />{tMenu('components.Layout.cerrar_sesion', ' Cerrar sesión')}
            </button>
            {/* B-870 · la versión del programa: lo que soporte pregunta primero. B-871 · y lleva a lo que trajo cada una,
                por ahora solo al equipo de Biango (7-oct-2026); a una clínica, el número sin enlace. */}
            {branding.version && (veNovedades ? (
              <Link to="/novedades" onClick={() => setPerfilOpen(false)} title={tMenu('components.Layout.novedades_por_version', 'Novedades por versión')}
                style={{ display: 'block', padding: '6px 12px 2px', fontSize: '11px', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                {tx('components.Layout.version', 'Versión {{version}}', { version: branding.version })}
              </Link>
            ) : (
              <span data-version-sin-enlace=""
                style={{ display: 'block', padding: '6px 12px 2px', fontSize: '11px', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                {tx('components.Layout.version', 'Versión {{version}}', { version: branding.version })}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  // M1: KIOSKO del Modo doctor — sin sidebar ni cabecera de la app; solo barra superior + agenda.
  if (kiosko) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'var(--alto-pantalla)', overflow: 'hidden', background: 'var(--bg)' }}>
        <header data-cabecera-app="kiosko" style={{ height: 'calc(56px + env(safe-area-inset-top, 0px))', background: 'var(--header-bg)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '12px', padding: 'env(safe-area-inset-top, 0px) 18px 0', flexShrink: 0 }}>
          {/* Aqui seguia vivo el mismo fallo que en el menu: /icon-192.png es el logotipo de UNA
              clinica, asi que cualquier otra veia el de otra empresa en la pantalla del gabinete. */}
          <MarcaBiango size={28} radio={8} />
          {/* EN EL MÓVIL NO CABÍA LA BARRA (B-050, lo mismo que en la cabecera normal). A 390 px el título se partía en
              dos líneas y «Salir del modo doctor» en tres, más alto que la propia barra. Por debajo de 560 px: el
              título no se parte (si no cabe, puntos), el tema pasa al menú del avatar (ya lo lleva cuando `estrecho`)
              y el botón dice solo «Salir», con el texto entero en su título y en su etiqueta. */}
          <span style={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: '15px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{tMenu('components.Layout.modo_doctor_2', 'Modo doctor')}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            {/* t271 · el reloj también aquí: el Modo doctor es la pantalla del gabinete, donde se mide un fraguado. */}
            <BotonReloj soloNumero={estrecho} />
            {!estrecho && <button onClick={toggleTema} title={dark ? tMenu('components.Layout.modo_claro', 'Modo claro') : tMenu('components.Layout.modo_oscuro', 'Modo oscuro')} style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--surface-2)', cursor: 'pointer', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Ico n={dark ? 'sun' : 'moon'} s={16} /></button>}
            <button onClick={() => navigate(['doctor', 'higienista', 'colaborador'].includes(rolMenu) ? '/agenda' : '/recepcion')} title={tMenu('components.Layout.salir_del_modo_doctor', 'Salir del modo doctor')} aria-label={tMenu('components.Layout.salir_del_modo_doctor', 'Salir del modo doctor')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 13px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}><Ico n="logout" s={15} /> {estrecho ? tMenu('components.Layout.salir', 'Salir') : tMenu('components.Layout.salir_del_modo_doctor', 'Salir del modo doctor')}</button>
            {/* TAMBIEN EN MODO DOCTOR. El doctor en quiosco es justamente a quien le avisan de que
                su certificado caduca, y aqui no hay ni menu ni panel: sin campanita se quedaria sin
                ningun sitio donde volver a leerlo. */}
            <CampanaAvisos />
            {perfilMenu}
          </div>
        </header>
        {/* Y EN EL QUIOSCO TAMBIEN, POR EL MOTIVO CONTRARIO AL DE LA BANDA DE ABAJO: aqui no
            hay ni menu ni cabecera donde apoyar ninguna pista, asi que si el aviso de «estas
            dentro de OTRA CLINICA» no saliera en el modo doctor, esta seria justo la pantalla en
            la que alguien de soporte escribiria en la ficha equivocada sin una sola señal. Va entre
            la cabecera y el <main>, fuera de lo que se desplaza: siempre a la vista y sin tapar nada
            (components/DentroDeClinica, 3-oct-2026). */}
        <DentroDeClinica />
        {/* TAMBIEN EN MODO DOCTOR, y por el mismo motivo que la campanita: quien trabaja en
            quiosco no ve ni el menu ni el panel, asi que si el aviso viviera solo en la pantalla
            normal seria justo el doctor —muchas veces el unico que hay en la clinica— quien no se
            enterase de que alguien de fuera esta dentro de los datos de sus pacientes. Fuera del <main>, sin tapar nada. */}
        {/* t262 · «Hay N citas por arreglar»: fijo arriba para quien gestiona citas (components/AvisoCitasPorArreglar). */}
        <AvisoCitasPorArreglar />
        <AvisoCuentaMeta />
        <AccesoSoporteBanner />
        <main data-scrolltop style={{ flex: 1, overflowY: 'auto', paddingBottom: 'var(--sa-abajo)' }}>
          <div style={{ padding: '0 16px' }}><ComprobacionesHoy /></div>
          <ErrorBoundary label={location.pathname}><Outlet /></ErrorBoundary>
        </main>
        <ChatWidget />
        <ChatNotifFlotante visible={chatALaVista} />
        <AvisoAdminBanner />
        <AvisoImpago />
        {/* También en Modo doctor: el doctor que trabaja en quiosco es justamente a quien avisan un
            domingo, así que es el último al que se le puede escapar el repaso. */}
        <RepasoUrgenciasModal />
        {!soporteDeVisita && VentanaFirma ? <VentanaFirma /> : null}
        {modalCambioDeClinica}
      </div>
    )
  }

  return (
    <div className={sidebarColapsado ? 'sidebar-colapsado' : ''} style={{ display: 'flex', height: 'var(--alto-pantalla)', overflow: 'hidden', background: 'var(--bg)' }}>

      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          zIndex: 'var(--capa-velo-menu-lateral)', backdropFilter: 'blur(2px)',
        }} className="hide-desktop" data-velo-menu-movil="" />
      )}

      <aside style={{
        width: '240px', background: 'var(--sidebar-bg)',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        position: 'fixed', top: 0, left: 0, height: 'var(--alto-pantalla)',
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1), margin-left 0.28s cubic-bezier(0.4,0,0.2,1)',
        zIndex: 'var(--capa-menu-lateral)', boxShadow: '4px 0 20px rgba(0,0,0,0.15)',
      }} id="sidebar">

        {/* iPhone/notch: el logo del cajón empieza DEBAJO del safe-area (si no, la hora lo tapaba) */}
        <div style={{ padding: 'calc(24px + env(safe-area-inset-top, 0px)) 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {/* ARRIBA VA EL PRODUCTO. Aqui estaba el icono y el nombre de la CLINICA, con lo que la
              aplicacion no se presentaba en ningun sitio: quien la usa a diario no llegaba a saber
              como se llama. Y el icono era ademas un fichero suelto (/icon-192.png) que es el de una
              clinica concreta, asi que TODAS las demas veian arriba el logo de otra empresa.

              Debajo, en pequeño, la clinica en la que se esta trabajando. No es adorno: es un
              producto multi-clinica y quitar del todo esa linea deja la pantalla sin decir sobre
              QUE clinica se esta actuando. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MarcaBiango size={36} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '15px', fontWeight: '700', color: 'white', letterSpacing: '-0.3px' }}>{MARCA_NOMBRE}</div>
              {branding.nombre && branding.nombre !== MARCA_NOMBRE && (
                <div title={branding.nombre} style={{ fontSize: '11px', color: 'var(--sidebar-text)', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '175px' }}>{branding.nombre}</div>
              )}
            </div>
          </div>
        </div>

        <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 10px' }}>
          {/* ORDENAR EL MENÚ. El interruptor va arriba y no en Ajustes: se ordena mirando el menú,
              y mandar a otra pantalla para colocar lo que se está viendo es dar un rodeo. */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 10px 6px' }}>
            <button type="button" onClick={() => setOrdenando(v => !v)}
              title={ordenando ? tMenu('components.Layout.terminar_de_ordenar', 'Terminar de ordenar') : tMenu('components.Layout.ordenar_el_menu_a_tu', 'Ordenar el menú a tu gusto (solo lo ves tú)')}
              style={{ background: ordenando ? 'var(--sidebar-active)' : 'transparent', border: 'none', borderRadius: 7,
                color: ordenando ? 'var(--sidebar-text-active)' : 'var(--sidebar-text)', fontSize: '10.5px',
                fontWeight: 700, cursor: 'pointer', padding: '4px 9px', letterSpacing: '.4px' }}>
              {ordenando ? tMenu('components.Layout.listo', 'Listo') : tMenu('components.Layout.ordenar', 'Ordenar')}
            </button>
          </div>
          {ordenando && (
            <div style={{ fontSize: '10.5px', color: 'var(--sidebar-text)', padding: '0 12px 8px', lineHeight: 1.5 }}>
              {tMenu('components.Layout.arrastra_las_paginas_dentro_de', 'Arrastra las páginas dentro de su grupo. Se guarda solo y es ')}<b>{tMenu('components.Layout.tuyo', 'tuyo')}</b>{tMenu('components.Layout.nadie_mas_lo_ve', ': nadie más lo ve.')}
            </div>
          )}
          {navItems.map(({ section, items }) => {
            // `oculto` fuera del menú: se llega a ellas desde dentro de su módulo… salvo la que es
            // la ÚNICA puerta de alguien — la lista de la compra del encargado que no tiene el
            // almacén (B-102). Sin esta excepción, ese permiso implícito existiría en el servidor y
            // no se vería por ningún sitio.
            const visibles = items.filter(i => (!i.oculto || seVeEnLugarDe(i, todasLasEntradas, moduloVisible) || (i.abreConEncargoCompras && soloSuListaCompra)) && moduloVisible(i))
            if (visibles.length === 0) return null
            // Secciones desplegables (cerradas por defecto): ALMACÉN y ADMINISTRACIÓN.
            // CHAT y DIFUSIÓN siempre abiertas.
            const esColapsable = false   // con el menú reagrupado ya no hace falta plegar nada
            const abierta = !esColapsable || secsAbiertas.has(section)
            const badgesSec = esColapsable ? visibles.reduce((sum, i) => sum + (badges[i.to] || 0), 0) : 0
            return (
            <div key={section} style={{ marginBottom: esColapsable ? '4px' : '8px' }}>
              <div
                onClick={esColapsable ? () => toggleSec(section) : undefined}
                style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  fontSize: '10px', fontWeight: '600',
                  // 5-oct-2026 · el menú va en el azul de la aplicación: todo lo que se lee, a 4,8:1 o más
                  // (index.css · --sidebar-text). Los blancos al 30-70 % eran para el menú casi negro de antes.
                  color: 'var(--sidebar-text)',
                  padding: '8px 10px 4px', letterSpacing: '1.2px',
                  cursor: esColapsable ? 'pointer' : 'default', userSelect:'none',
                }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:'6px' }}>
                  {tDato(section)}
                  {esColapsable && !abierta && badgesSec > 0 && (
                    <span style={{ display:'inline-block', width:'7px', height:'7px', background:'#dc2626', borderRadius:'50%' }} />
                  )}
                </span>
                {esColapsable && (
                  <span style={{ fontSize:'10px', opacity:0.7, transform: abierta ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▾</span>
                )}
              </div>
              {abierta && ordenDe(section, visibles.map(v => v.to)).map(t => visibles.find(v => v.to === t)).filter(Boolean).map(({ to, icon, label }) => (
                <NavLink key={to} to={to === '/administracion' ? (entradaAdmin || to) : to}
                  draggable={ordenando}
                  onDragStart={ordenando ? (() => { arrastrando.current = to }) : undefined}
                  onDragOver={ordenando ? (ev => ev.preventDefault()) : undefined}
                  onDrop={ordenando ? (ev => { ev.preventDefault(); soltar(section, visibles.map(v => v.to), to) }) : undefined}
                  // Mientras se ordena, pulsar una entrada NO debe navegar: se está colocando, no
                  // yendo a ninguna parte, y un clic mal interpretado saca de la pantalla a mitad.
                  onClick={ordenando ? (ev => ev.preventDefault()) : undefined}
                  style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '9px 10px', fontSize: '13px', borderRadius: '8px',
                  marginBottom: '2px',
                  // 5-oct-2026 · LA ENTRADA ACTIVA, CON EL COLOR SECUNDARIO: el de «Datos y marca», o el calculado del
                  // principal, o el de Biango (#465066), y encima el texto que le toca, blanco u oscuro, el que más
                  // contraste dé (index.css · --secundario y --sobre-secundario; utils/contraste · contrasteSecundario).
                  background: isActive ? 'var(--secundario)' : 'transparent',
                  color: isActive ? 'var(--sobre-secundario)' : 'var(--sidebar-text)',
                  fontWeight: isActive ? '500' : '400',
                  transition: 'all 0.12s',
                  borderLeft: isActive ? '2px solid var(--secundario)' : '2px solid transparent',
                })}>
                  {/* El asa solo aparece en modo ordenar: un icono de arrastre permanente ensucia
                      treinta entradas para algo que se hace una vez. */}
                  {ordenando && <span style={{ opacity: 0.5, flexShrink: 0, cursor: 'grab', fontSize: '13px' }} title={tMenu('components.Layout.arrastra_para_mover', 'Arrastra para mover')}>⠿</span>}
                  <span style={{ opacity: 0.8, flexShrink: 0 }}>{icon}</span>
                  <span style={{ flex: 1 }}>{to === '/laboratorio' ? tMenu('components.Layout.laboratorio', 'Laboratorio') : to === '/pacientes' ? tMenu('components.Layout.pacientes', 'Pacientes') : tDato(label)}</span>
                  {badges[to] > 0 && (
                    <span style={{ 
                      background: '#dc2626', color: 'white', borderRadius: '10px',
                      padding: '1px 7px', fontSize: '11px', fontWeight: '700',
                      minWidth: '18px', textAlign: 'center', lineHeight: '14px',
                      boxShadow: '0 1px 3px rgba(220,38,38,0.4)'
                    }}>
                      {badges[to] > 99 ? '99+' : badges[to]}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          )})}
        </nav>

        {/* El final del menú llega al borde de la pantalla: en Android, debajo de la barra del sistema (index.css, --sa-abajo). */}
        <div style={{ padding: '12px 10px calc(20px + var(--sa-abajo))', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '10px', borderRadius: '8px',
            background: 'var(--sidebar-hover)', marginBottom: '8px',
          }}>
            {/* AQUI EL AVATAR NO LLEVABA A NINGUN SITIO, y un punto encima de algo que no se puede
                pulsar es peor que no ponerlo: se ve el aviso y no hay forma de ir a atenderlo. Se
                convierte en el enlace a Mi perfil, que es adonde manda el aviso. */}
            <Link to="/perfil" title={tMenu('components.Layout.mi_perfil', 'Mi perfil')} style={{ display: 'inline-flex', flexShrink: 0 }}>
              <AvatarPorFirmar>
                {user?.avatar
                  ? <img src={user.avatar} alt="" style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  : <div style={{
                    width: '30px', height: '30px', borderRadius: '50%',
                    background: 'var(--sidebar-active)',
                    color: 'var(--sidebar-text-active)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0,
                  }}>
                    {user?.nombre?.[0]?.toUpperCase()}
                  </div>}
              </AvatarPorFirmar>
            </Link>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '12px', fontWeight: '500', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.nombre} {user?.apellidos}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--sidebar-text)' }}>{nombreRol(!user?._ver_como && user?.rol_real ? user.rol_real : user?.rol)}</div>
            </div>
          </div>
          {/* t93 · «Cambiar de clínica», debajo del nombre: solo a quien su contraseña le abrió más de una al entrar. */}
          <CambioDeClinica clinicas={otrasClinicas} ocupada={cambiandoA} onElegir={cambiarDeClinica} oscuro />
          {/* Y aquí, debajo, «Cambiar de usuario» (la pantalla del PIN, t95), antes de «Cerrar sesión». */}
          <CambioDeUsuario oscuro />
          {/* t478 · y aquí, solo si hay otra cuenta abierta en el dispositivo: pasar a ella con un toque. */}
          <MenuDeCuentas oscuro soloSiHayOtras />
          <button onClick={handleLogout} style={{
            width: '100%', padding: '8px', background: 'var(--sidebar-hover)',
            color: 'var(--sidebar-text)', borderRadius: '8px', fontSize: '12px',
            transition: 'all 0.12s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            {tMenu('components.Layout.cerrar_sesion_2', 'Cerrar sesión')}
          </button>
          {/* B-870 · la versión del programa, también aquí: la barra está siempre a la vista. B-871 · lleva a sus novedades,
              por ahora solo al equipo de Biango (7-oct-2026); a una clínica, el número sin enlace. */}
          {branding.version && (veNovedades ? (
            <Link to="/novedades" title={tMenu('components.Layout.novedades_por_version', 'Novedades por versión')}
              style={{ display: 'block', marginTop: '6px', textAlign: 'center', fontSize: '10.5px', color: 'var(--sidebar-text)', fontVariantNumeric: 'tabular-nums' }}>
              {tx('components.Layout.version', 'Versión {{version}}', { version: branding.version })}
            </Link>
          ) : (
            <span data-version-sin-enlace=""
              style={{ display: 'block', marginTop: '6px', textAlign: 'center', fontSize: '10.5px', color: 'var(--sidebar-text)', fontVariantNumeric: 'tabular-nums' }}>
              {tx('components.Layout.version', 'Versión {{version}}', { version: branding.version })}
            </span>
          ))}
        </div>
      </aside>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', marginLeft: 0 }} id="main-content">
        <header ref={cabeceraRef} style={{
          // En iPhone (notch/Dynamic Island) la cabecera quedaba bajo el notch y el botón
          // hamburguesa no se podía pulsar. Empujamos el contenido con safe-area-inset-top.
          height: 'calc(58px + env(safe-area-inset-top, 0px))', background: 'var(--header-bg)',
          borderBottom: '1px solid var(--border)', boxSizing: 'border-box',
          display: 'flex', alignItems: 'center',
          // EL SAFE-AREA, TAMBIEN A LOS LADOS. Solo se contaba el de arriba. Con el telefono
          // TUMBADO la muesca vale 44-59 px A UN LADO, y con 20 px en crudo el boton de menu (o el
          // avatar, segun de que lado se tumbe) quedaba DEBAJO del recorte de la pantalla: ni se ve
          // entero ni el sistema deja pulsarlo.
          padding: 'env(safe-area-inset-top, 0px) calc(20px + env(safe-area-inset-right, 0px)) 0 calc(20px + env(safe-area-inset-left, 0px))', gap: '14px',
          // t302 · SU CAPA, DE LA ESCALA DE index.css («LAS CAPAS DE LA APLICACIÓN»): encima de la página y de sus avisos
          // («Hueco antes»), debajo del menú lateral del móvil; con un desplegable abierto sube sola (`data-cabecera-app`).
          position: 'sticky', top: 0, zIndex: 'var(--capa-cabecera)', flexShrink: 0,
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }} data-cabecera-app="principal">
          <button onClick={toggleSidebar} title={tMenu('components.Layout.mostrar_ocultar_menu', 'Mostrar/ocultar menú')} style={{
            background: 'none', padding: '6px', borderRadius: '8px',
            color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
            transition: 'background 0.12s', position: 'relative',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
            {/* B-606 · la suma de lo que espera detrás del menú escondido. */}
            {totalBadges > 0 && (
              <span className="chapa-menu" aria-label={tMenu('components.Layout.avisos_sin_ver_en_el', '{{totalBadges}} avisos sin ver en el menú', { totalBadges })} style={{
                position: 'absolute', top: '-1px', right: '-2px', minWidth: '16px', height: '16px', padding: '0 4px',
                borderRadius: '9px', background: 'var(--danger)', color: '#fff', fontSize: '10px', fontWeight: 800,
                alignItems: 'center', justifyContent: 'center', lineHeight: 1, boxSizing: 'border-box',
              }}>{totalBadges > 99 ? '99+' : totalBadges}</span>
            )}
            {/* B-606 · Y UNA MARCA PROPIA PARA UN CASO DERIVADO SIN ACEPTAR. La chapa de arriba es la SUMA de todas: WhatsApp sin
                leer, chat, tablón, urgencias, almacén, cirugías, consentimientos, asistencias y derivaciones. En una clínica con
                veinte WhatsApps sin leer, un caso nuevo movía la chapa de 20 a 21 y no lo notaba nadie; y si luego alguien leía un
                WhatsApp, volvía a 20 con el caso todavía sin aceptar. Un caso lo manda un profesional de FUERA que está esperando,
                así que tiene su propio punto: no puede esconderse dentro de una suma. */}
            {(derivPend?.total || 0) > 0 && (
              <span className="chapa-menu punto-derivaciones"
                aria-label={tMenu('components.Layout.plural_casos_derivados_esperando', { one: '{{count}} caso derivado esperando', other: '{{count}} casos derivados esperando' }, { count: derivPend.total, _forma: (derivPend.total === 1) ? 'one' : 'other' })}
                title={tMenu('components.Layout.plural_casos_derivados_esperando', { one: '{{count}} caso derivado esperando', other: '{{count}} casos derivados esperando' }, { count: derivPend.total, _forma: (derivPend.total === 1) ? 'one' : 'other' })}
                style={{
                  position: 'absolute', bottom: '-1px', right: '-2px', width: '9px', height: '9px', borderRadius: '50%',
                  background: 'var(--primary)', border: '1.5px solid var(--surface)', boxSizing: 'border-box',
                }} />
            )}
          </button>

          {/* En MOVIL el header ya no lleva ni logo ni siglas (peticion del dueño): cada pixel de
              ancho es para el buscador y las acciones, y la marca ya vive en la cabecera del menu
              lateral (logo + nombre completo), que es donde se mira al abrirlo. */}

          {!sinRotulos && (
            <span style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '15px' }}>
              {pageName}
            </span>
          )}
          {/* t578 · «CÓMO SE HACE»: el vídeo (o los vídeos) de la Formación de ESTA pantalla, junto a su nombre. Lo decide el dato
              (GET /api/formacion/como-se-hace) con la dirección: una pantalla sin vídeo no pinta nada. Donde la barra va
              justa, el icono solo. El quiosco de fichar no tiene Formación. */}
          {user?.rol !== 'control_horario' && <ComoSeHace ruta={location.pathname} busqueda={location.search} compacto={sinRotulos} enMenu={comoAlMenu} />}

          {/* t271b · en el móvil, el hueco del buscador con menos margen a los lados (4 px y no 12): esos 16 px son los que le
              faltaban a «Buscar» en un teléfono de 360 px con todo lo demás ya en el menú del avatar. */}
          {/* BUSCADOR DE PACIENTE SIEMPRE A MANO (ocupa el centro de la cabecera). Antes habia que ir
              a la pagina de Pacientes para abrir una ficha; desde la agenda o un chat eran dos saltos. */}
          {/* TAMBIEN EN MOVIL. Estaba dentro de `hide-mobile`, asi que en el movil —donde mas duele
              tener que teclear un nombre entero— no habia ni buscador ni lista de fichas abiertas
              hoy. Ocupa menos ancho ahi, pero se llega igual. */}
          <div ref={huecoBuscadorRef} style={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center', minWidth: 0, padding: estrecho ? '0 4px' : '0 12px' }}>
            <BuscadorPacienteGlobal />
          </div>

          <div ref={accionesCabeceraRef} style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* "Ver como rol": el admin previsualiza el sistema como otro rol desde CUALQUIER pantalla
                (no solo en el panel). Se auto-oculta a quien no es admin. */}
            {!sinVerComo && <VerComoSelector />}
            {/* t271 · EL RELOJ: reloj, cuenta atrás y cronómetro en una ventana encima de la página (utils/relojCabecera). Con
                algo en marcha enseña lo que queda; en la tablet y el móvil (`compacto`), el número solo, para que quepa. */}
            {!relojAlMenu && <BotonReloj soloNumero={compacto} />}
            {!temaAlMenu && <button onClick={toggleTema} title={dark ? tMenu('components.Layout.cambiar_a_modo_claro', 'Cambiar a modo claro') : tMenu('components.Layout.cambiar_a_modo_oscuro', 'Cambiar a modo oscuro')} aria-label={tMenu('components.Layout.cambiar_tema', 'Cambiar tema')}
              style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--surface-2)', cursor: 'pointer', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, flexShrink: 0 }}>
              <Ico n={dark ? 'sun' : 'moon'} s={16} />
            </button>}
            {/* N15: chat INTERNO del equipo a pantalla completa (separado del widget flotante de ayuda).
                Botón llamativo (relleno de color de marca) para que se vea. Lleva a /chat: el globo
                flotante se queda para AYUDA/soporte, que es lo que cabe en 400x650. */}
            {/* Estando YA dentro del chat, el mismo botón SALE: vuelve a donde estabas antes de entrar
                (y si no hay a dónde volver, a la agenda). Antes pulsarlo dentro del chat no hacía
                nada y había que buscar el menú para salir. Se pinta como una X para que se vea que
                es la salida y no "abrir el chat otra vez". */}
            {/* EL MISMO CRITERIO QUE EL MENU, NO UNO PROPIO. Este boton se pintaba solo con
                `permiso('chat')`, y la clave `chat` la trae de fabrica TODO el equipo — incluido el
                rol almacen, cuyo menu NO lista /chat. Resultado, cazado por un esceptico: al
                almacenista se le pintaba el boton y al pulsarlo la puerta de ruta lo echaba a
                /inicio. Un boton que expulsa es peor que un boton que falta. Preguntando por la
                MISMA funcion que decide el menu, los dos dicen lo mismo siempre — y el dia que el
                dueño revise los defaults, los dos cambian a la vez. */}
            {/* t271b · su letra y su icono, con `--sobre-primary` (en oscuro, la tinta: el blanco sobre el gris claro no se leía). */}
            {moduloVisible({ to: '/chat', roles: (navItems.flatMap(s => s.items).find(m => m.to === '/chat') || {}).roles }) && <button
              onClick={() => {
                if (!enChat) { volverDeChat.current = location.pathname + location.search; navigate('/chat'); return }
                const destino = volverDeChat.current
                volverDeChat.current = null
                navigate(destino && !destino.startsWith('/chat') ? destino : '/agenda')
              }}
              title={enChat ? tMenu('components.Layout.salir_del_chat_y_volver', 'Salir del chat y volver a donde estabas') : tMenu('components.Layout.chat_del_equipo_mensajes_internos', 'Chat del equipo (mensajes internos)')}
              style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 13px', borderRadius: '9px', border: 'none', background: enChat ? 'var(--danger)' : 'var(--primary)', color: enChat ? '#fff' : 'var(--sobre-primary)', fontSize: '12.5px', fontWeight: 800, cursor: 'pointer', flexShrink: 0, boxShadow: `0 2px 8px color-mix(in srgb, ${enChat ? 'var(--danger)' : 'var(--primary)'} 35%, transparent)` }}>
              {enChat ? <span style={{ fontSize: '15px', lineHeight: 1, fontWeight: 700 }}>&times;</span> : <Ico n="send" s={16} />}
              {!sinRotulos && <span>{enChat ? tMenu('components.Layout.salir_del_chat', 'Salir del chat') : tMenu('components.Layout.chat_equipo', 'Chat equipo')}</span>}
              {chatNoLeidos > 0 && <span style={{ position: 'absolute', top: '-6px', right: '-6px', minWidth: '18px', height: '18px', padding: '0 4px', borderRadius: '9px', background: 'var(--danger)', color: '#fff', fontSize: '10.5px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, border: '2px solid var(--header-bg)' }}>{chatNoLeidos > 99 ? '99+' : chatNoLeidos}</span>}
            </button>}
            {/* B-875 · el Busca, a un toque: a quién buscas y su app vibra hasta que contesta. En el móvil, en el menú del perfil. */}
            {chatALaVista && !ayudaAlMenu && (
              <button data-abrir-busca onClick={abrirBusca} title={tMenu('components.Layout.busca_avisar_a_alguien_de', 'Busca: avisar a alguien de que le buscas')}
                style={{ width: '32px', height: '32px', borderRadius: '9px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, padding: 0 }}>
                <Ico n="megaphone" s={16} />
              </button>
            )}
            {/* M1/M2: accesos a Modo doctor (doctores con agenda + admin) y Modo auxiliar (auxiliar + admin) */}
            {puedeModoDoctor && !modosAlMenu && (
              <button
                onClick={async () => {
                  // Sin agenda asignada, Modo doctor no tiene nada que enseñar. Antes el botón
                  // simplemente no se pintaba y quien lo estaba configurando no tenía forma de
                  // saber que le faltaba ESE paso.
                  if (modoDoctorSinAgenda) {
                    await avisar({
                      titulo: tMenu('components.Layout.falta_asignarle_su_agenda', 'Falta asignarle su agenda'),
                      mensaje: tMenu('components.Layout.este_usuario_tiene_permiso_de', 'Este usuario tiene permiso de Modo doctor, pero no tiene ninguna agenda asignada, así que no habría citas que mostrar.'),
                      detalle: tMenu('components.Layout.un_administrador_puede_asignarsela_en', 'Un administrador puede asignársela en Administración → Usuarios, en el apartado de agendas de ese usuario. En cuanto se guarde, el botón funciona sin necesidad de volver a entrar.'),
                    })
                    return
                  }
                  navigate('/doctor')
                }}
                title={modoDoctorSinAgenda ? tMenu('components.Layout.modo_doctor_falta_asignarle_una', 'Modo doctor — falta asignarle una agenda') : tMenu('components.Layout.modo_doctor_pantalla_completa_de', 'Modo doctor — pantalla completa de tu agenda')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 11px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: modoDoctorSinAgenda ? 'var(--text-muted)' : 'var(--text-secondary)', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', flexShrink: 0, opacity: modoDoctorSinAgenda ? .55 : 1 }}>
                <Ico n="calendarCheck" s={15} /> {!sinRotulos && <span>{tMenu('components.Layout.modo_doctor_2', 'Modo doctor')}</span>}
              </button>
            )}
            {puedeModoAuxiliar && !modosAlMenu && (
              <button onClick={() => navigate('/auxiliar')} title={tMenu('components.Layout.modo_auxiliar_2', 'Modo auxiliar')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 11px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-secondary)', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                <Ico n="users" s={15} /> {!sinRotulos && <span>{tMenu('components.Layout.modo_auxiliar_2', 'Modo auxiliar')}</span>}
              </button>
            )}
            {/* El «?»: cómo contactar con Biango (correo, teléfono, web y la app en las tiendas). En el móvil
                no cabe aquí: va en el menú del avatar (utils/contactoBiango.jsx). */}
            {!ayudaAlMenu && <ContactoBiango />}
            {/* LA CAMPANITA. Lo que el modal de entrada ya no interrumpe (mas de 24 h) se queda
                aqui: es la unica esquina que ve TODO el mundo, en cualquier pantalla. */}
            <CampanaAvisos />
            {perfilMenu}
          </div>
        </header>
        {/* t271b · con el reloj en el menú del avatar (no cabía en la barra), lo que está en marcha sale aquí, en una franja
            debajo de la barra; sin nada en marcha no se ve (utils/relojCabecera · FranjaReloj). Va SIN `&&` a propósito: varias
            guardias ejecutan este tramo entre la cabecera y el <main> sin las variables de la pantalla (test-helpers/ramaJsx). */}
        <FranjaReloj activa={relojAlMenu} />

        {/* LO PRIMERO DE TODO, ANTES INCLUSO DE LA PETICION DE ENTRADA: si quien mira esta
            pantalla es soporte metido en OTRA clinica, eso cambia el significado de TODO lo que
            hay debajo. Va entre la cabecera y el <main>, FUERA de lo que se desplaza: siempre a la
            vista, y empuja el contenido en vez de taparlo (components/DentroDeClinica, 3-oct-2026). */}
        <DentroDeClinica />
        {/* Y LA DE LA CLINICA, EN TODAS LAS PANTALLAS: que alguien de Biango esta dentro de los datos de los pacientes no
            puede vivir en una esquina ni detras de un menu, y la puede cortar quien este delante, sea quien sea. Tambien
            entre la cabecera y el <main>: siempre a la vista y sin tapar nada (components/AccesoSoporteBanner, 3-oct-2026). */}
        {/* t262 · «Hay N citas por arreglar»: fijo arriba para quien gestiona citas (components/AvisoCitasPorArreglar). */}
        <AvisoCitasPorArreglar />
        <AvisoCuentaMeta />
        <AccesoSoporteBanner />

        <main data-scrolltop style={{ flex: 1, overflowY: 'auto', padding: '24px 20px calc(24px + var(--sa-abajo))' }}>
          {/* B-2238 · LA PILA DE AVISOS DE LA PÁGINA: los «Hueco antes» y las comprobaciones del día, uno debajo de otro y
              con 24 px por debajo (index.css, `.avisos-pagina`). Esos 24 px son los que la ficha, Comunicación, el chat del
              equipo y Formación se suben para llegar al borde: así empiezan justo debajo del último aviso en vez de meterse
              por debajo. Sin avisos la pila no pinta nada y no deja hueco. Lo mide backend/routes/aviso-huecos-no-tapa.test.js. */}
          <div data-avisos-pagina="" className="avisos-pagina">
          <BannerHuecosLista />
          {/* B-672 · LAS COMPROBACIONES DEL DIA, AQUI Y NO EN UNA PANTALLA. Vivian solo en /dashboard, que
              solo abren administracion, recepcion y auxiliares: una comprobacion puesta a un doctor, a
              una higienista o a limpieza no le salia NUNCA, y la de recepcion solo si estaba en esa
              pantalla a esa hora. Salen donde este quien las tiene, y solo cuando ya es su hora. */}
          <ComprobacionesHoy />
          </div>
          <ErrorBoundary label={location.pathname}><Outlet /></ErrorBoundary>
          {/* B-172 · la guía de la primera vez de esta pantalla (utils/guias). En una visita del soporte, no. */}
          <GuiaDePantalla ruta={location.pathname} preferencias={prefsMenu} desactivada={!!soporteDeVisita} />
        </main>
      </div>
      {/* LOS ERRORES DE ENVIO SON DE LA CLINICA, Y EN UNA VISITA DEL SOPORTE NO SE MONTAN (B-504). Su
          ventana los da por vistos para TODO el equipo, y el soporte solo mira: no le toca apagarle a
          la clinica un aviso que nadie de alli ha leido. Va fuera del <main> porque solo pinta cosas
          fijas (el globo y su ventana), y asi la condicion no se mezcla con lo que se mide del <main>. */}
      {!soporteDeVisita && <AlertasEnvios />}
      <ChatWidget />
      <ChatNotifFlotante visible={chatALaVista} />
      {user?.es_superadmin === true && <AvisoSoporteDelEquipo />}
      <NotifPersonal />
      <AvisoAdminBanner />
      <AvisoImpago />
      {/* El día del repaso, la lista de urgencias salta al entrar (a recepción con todos, al doctor
          con los suyos). Mismo sitio que los avisos del admin: lo que hay que ver al entrar se monta
          aquí, no en cada pantalla. */}
      <RepasoUrgenciasModal />
      {!soporteDeVisita && VentanaFirma ? <VentanaFirma /> : null}
      {modalCambioDeClinica}

      <style>{`
        /* B-606 · en móvil el menú lateral está fuera de pantalla hasta que se pulsa: el número va siempre. */
        .chapa-menu { display: inline-flex; }
        /* B-868 · con el ANCHO EFECTIVO (clases de utils/uiScale), no con @media: con el tamaño «Muy grande» del perfil el
           menú fijo de escritorio no cabe y tiene que comportarse como en el móvil. :where() no cambia la especificidad. */
        :where(html.a-min-769) #sidebar { transform: translateX(0) !important; position: relative !important; }
        :where(html.a-min-769) .sidebar-colapsado #sidebar { margin-left: -240px !important; }
        /* Con el menú a la vista el número ya está en su entrada: aquí sobraría. */
        :where(html.a-min-769) .chapa-menu { display: none; }
        :where(html.a-min-769) .sidebar-colapsado .chapa-menu { display: inline-flex; }
        /* La entrada ACTIVA no cambia al pasar el ratón: lleva el color secundario y su texto, y el blanco translúcido
           de aquí la apagaría (y con un secundario claro dejaría su texto blanco sobre claro). */
        #sidebar a:not(.active):hover { background: var(--sidebar-hover) !important; color: white !important; }
        .btn-primary:active { transform: scale(0.98); }
      `}</style>
    </div>
  )
}
